import React, { useState, useEffect } from 'react';
import { 
  Calendar,
  AlertTriangle,
  CheckCircle,
  Bell,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  FileCheck,
  Building2,
  CheckCircle2,
  Clock,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  XCircle,
  Maximize2,
  Sparkles,
  Info,
  Package,
  FileText,
  RefreshCw,
  ClipboardList,
  Pencil,
  Save,
  X as XIcon,
  RotateCcw,
  Download,
  Image,
  Shield,
  BarChart3,
  Timer,
  UserCheck,
  User
} from 'lucide-react';
import { getTasksForOrganization, TaskWithDetails, getTaskCompletionDetails, completeTask, getUserRole } from '@/services/taskService';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useLocationFilter } from '@/contexts/LocationFilterContext';
import { useNavigate, useLocation } from 'react-router-dom';
import CompleteTaskModal, { TaskCompletionData } from './CompleteTaskModal';
import ReviewTaskModal from './ReviewTaskModal';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { locationService } from '@/firebase/locationService';
import { calculateLocationScore, getScoreBadgeClass, LocationScore } from '@/services/complianceScoreService';
import { normalizeSubscriptionPlan } from '@/firebase/subscriptionService';
import { 
  fetchUserRoles, 
  taskMatchesUserOwnerRoles, 
  isSystemManager, 
  normalizeRoles,
  getUserSystemRoleForOrganization,
  getUserOwnerRolesForOrganization,
  isSystemUserInOrganization,
  isManagerInOrganization,
  isAdminInOrganization
} from '@/utils/roleHelpers';
import { usePreview } from '@/contexts/PreviewContext';
import { useOrganizationType } from '@/hooks/useOrganizationType';
import { assetService, Asset, AssetType } from '@/firebase/assetService';
import { checklistCompletionService, ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { checklistService, Checklist, ChecklistItem } from '@/firebase/checklistService';
import { checklistEvidenceService, ChecklistEvidence } from '@/firebase/checklistEvidenceService';
import { assetFactsService } from '@/firebase/assetFactsService';
import { extractEvidenceFieldsForItem, evaluateWorkflowOnlyEvidence } from '@/services/autoCompleteChecklistService';
import { evaluateChecklistItemWithAI, evaluateFieldWorkflowRuleWithAI, fileNameMatchesKey, getFileNameKeys, fileNameMatchesAnyKey } from '@/services/geminiService';
import { serverTimestamp } from 'firebase/firestore';
import * as XLSX from 'xlsx';

/** Compliance review (Bank): item needs reviewer action */
function isActionableChecklistItemStatus(status: string | undefined): boolean {
  return status === 'NEEDS_REVIEW' || status === 'FAIL';
}

function doesChecklistFieldNeedReview(
  itemText: string,
  fieldKey: string,
  evidences: ChecklistEvidence[],
  assetFacts: Record<string, any>,
  fieldDef: { required?: boolean },
  completion: ChecklistCompletion
): boolean {
  const summaryMissing = completion.completionSummary?.missingRequiredFields?.find((m) => m.itemText === itemText);
  if (summaryMissing?.missingFields?.includes(fieldKey)) return true;
  for (const ev of evidences) {
    if (ev.fieldStatus?.missingFields?.includes(fieldKey)) return true;
    if (ev.fieldStatus?.invalidFields?.includes(fieldKey)) return true;
    const fv = ev.fieldValues?.[fieldKey];
    if (fv && fv.validated === false) return true;
  }
  if (fieldDef.required) {
    const fact = assetFacts[fieldKey];
    const hasVal =
      fact &&
      ((fact.value !== null && fact.value !== undefined && fact.value !== '') ||
        (fact.extractedValues && fact.extractedValues.length > 0) ||
        fact.source === 'REVIEWER_OVERRIDE');
    if (!hasVal) return true;
  }
  return false;
}

/** Evidence block still needs work (actionable filter) */
function checklistEvidenceNeedsAttention(ev: ChecklistEvidence): boolean {
  if (ev.validationStatus === 'COMPLETE') {
    return (ev.fieldStatus?.invalidFields?.length ?? 0) > 0;
  }
  if (ev.validationStatus === 'INVALID' || ev.validationStatus === 'INCOMPLETE') return true;
  if ((ev.fieldStatus?.missingFields?.length ?? 0) > 0) return true;
  if ((ev.fieldStatus?.invalidFields?.length ?? 0) > 0) return true;
  if (ev.fieldValues) {
    for (const fd of Object.values(ev.fieldValues)) {
      if (fd && fd.populated && fd.validated === false) return true;
    }
  }
  if (!ev.validationStatus && !ev.fieldStatus && !ev.fieldValues) return true;
  return false;
}

/** Front-end demo metrics for Bank Compliance Review until analytics API exists */
const BANK_COMPLIANCE_REVIEW_DEMO_KPIS = {
  activeLoanFiles: 248,
  passedQC: 186,
  needsReview: 24,
  failedExceptions: 3,
  pendingInitialRun: 35,
  avgDaysToClear: 4.2,
  passRateLast30d: 94,
  autoRunCoveragePct: 88,
  exceptionsTrendVsPrior: -12,
  filesReviewedMTD: 112,
} as const;

// FieldEditModal component for editing field values
interface FieldEditModalProps {
  field: { itemText: string; fieldKey: string; fieldDef: any };
  asset: Asset;
  availableFiles: Array<{ id: string; name: string }>;
  assetFacts: Record<string, any>;
  currentValue: { value: any; file: string };
  onSave: (newValue: any, selectedFile: string) => Promise<void>;
  onCancel: () => void;
}

const FieldEditModal: React.FC<FieldEditModalProps> = ({ field, availableFiles, assetFacts, currentValue, onSave, onCancel }) => {
  // Initialize value properly - handle Date objects and convert Timestamps
  const getInitialValue = () => {
    if (!currentValue.value) return null;
    // If it's already a Date object, use it
    if (currentValue.value instanceof Date) {
      return isNaN(currentValue.value.getTime()) ? null : currentValue.value;
    }
    // If it's a Timestamp-like object, convert it
    if (currentValue.value && typeof currentValue.value.toDate === 'function') {
      try {
        return currentValue.value.toDate();
      } catch (e) {
        return null;
      }
    }
    // If it's a string that looks like a date, try to parse it
    if (typeof currentValue.value === 'string') {
      const date = new Date(currentValue.value);
      return isNaN(date.getTime()) ? currentValue.value : date;
    }
    return currentValue.value;
  };
  
  const [value, setValue] = useState<any>(getInitialValue());
  const [selectedFile, setSelectedFile] = useState<string>(currentValue.file);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showOnlyWithValues, setShowOnlyWithValues] = useState<boolean>(false);
  
  // Get all extracted values for this field from asset facts
  const fact = assetFacts[field.fieldKey];
  const isManuallyUpdated = fact?.source === 'REVIEWER_OVERRIDE';
  const overrideBy = fact?.overriddenBy;
  const overrideAt = fact?.overriddenAt;
  const overrideReason = fact?.overrideReason;
  const extractedValuesByFile: Array<{ fileName: string; value: any; confidence: string }> = [];
  
  if (fact) {
    if (fact.extractedValues && fact.extractedValues.length > 0) {
      // Filter by fileNameKey/fileNameKeys if specified
      let valuesToUse = fact.extractedValues;
      
      const fileNameKeys = getFileNameKeys(field.fieldDef);
      if (fileNameKeys.length > 0) {
        // Only use values from files matching any of the fileNameKeys
        valuesToUse = fact.extractedValues.filter((ev: any) => {
          if (!ev.fileName) return false;
          return fileNameMatchesAnyKey(ev.fileName, fileNameKeys);
        });
        
        if (valuesToUse.length === 0) {
          // No matching values found, but log for debugging
          console.debug(
            `[FieldEditModal] Field "${field.fieldDef.label || field.fieldKey}" has fileNameKeys [${fileNameKeys.join(', ')}] ` +
            `but no extracted values match. Available files: ${fact.extractedValues.map((ev: any) => ev.fileName).join(', ')}`
          );
          // Fall back to all values for backward compatibility
          valuesToUse = fact.extractedValues;
        } else {
          console.log(
            `[FieldEditModal] Filtered ${fact.extractedValues.length} extracted values to ${valuesToUse.length} ` +
            `matching fileNameKeys [${fileNameKeys.join(', ')}] for field "${field.fieldDef.label || field.fieldKey}"`
          );
        }
      }
      
      // Get filtered extracted values
      valuesToUse.forEach((ev: any) => {
        if (ev.fileName && ev.value !== null && ev.value !== undefined && ev.value !== '') {
          extractedValuesByFile.push({
            fileName: ev.fileName,
            value: ev.value,
            confidence: ev.confidence || 'low'
          });
        }
      });
    }
  }
  
  // Helper to format override date
  const formatOverrideDate = (dateValue: any): string => {
    if (!dateValue) return '';
    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }
    if (dateValue && typeof dateValue.toDate === 'function') {
      try {
        return dateValue.toDate().toLocaleDateString();
      } catch (e) {
        return '';
      }
    }
    if (typeof dateValue === 'object' && dateValue !== null) {
      if (dateValue.seconds !== undefined || dateValue._seconds !== undefined) {
        try {
          const seconds = dateValue.seconds || dateValue._seconds || 0;
          return new Date(seconds * 1000).toLocaleDateString();
        } catch (e) {
          return '';
        }
      }
    }
    return '';
  };
  
  // Helper to extract user from reason
  const extractUserFromReason = (reason: string): string => {
    if (!reason) return '';
    if (reason.includes('by ')) {
      const userPart = reason.split('by ')[1];
      if (userPart) {
        const userInfo = userPart.split(' from')[0] || userPart.split(' on')[0] || userPart;
        return userInfo.trim();
      }
    }
    return reason;
  };
  
  // Create a map of file name to extracted value
  const fileValueMap = new Map<string, { value: any; confidence: string }>();
  extractedValuesByFile.forEach(item => {
    fileValueMap.set(item.fileName, { value: item.value, confidence: item.confidence });
  });
  
  // Filter and group files
  // First, filter by fileNameKey/fileNameKeys if specified
  let filesToShow = availableFiles;
  
  const fileNameKeys = getFileNameKeys(field.fieldDef);
  if (fileNameKeys.length > 0) {
    // Filter available files by fileNameKeys (matches any key)
    filesToShow = availableFiles.filter(file => {
      return fileNameMatchesAnyKey(file.name, fileNameKeys);
    });
    
    if (filesToShow.length === 0) {
      // No matching files found, but show all for backward compatibility
      console.debug(
        `[FieldEditModal] Field "${field.fieldDef.label || field.fieldKey}" has fileNameKeys [${fileNameKeys.join(', ')}] ` +
        `but no files match. Available files: ${availableFiles.map(f => f.name).join(', ')}`
      );
      filesToShow = availableFiles; // Fall back to all files
    } else {
      console.log(
        `[FieldEditModal] Filtered ${availableFiles.length} files to ${filesToShow.length} ` +
        `matching fileNameKeys [${fileNameKeys.join(', ')}] for field "${field.fieldDef.label || field.fieldKey}"`
      );
    }
  }
  
  // Then apply search and "show only with values" filters
  const filteredFiles = filesToShow.filter(file => {
    const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (showOnlyWithValues) {
      return fileValueMap.has(file.name);
    }
    return true;
  });
  
  // Separate files with and without values
  const filesWithValues = filteredFiles.filter(f => fileValueMap.has(f.name));
  const filesWithoutValues = filteredFiles.filter(f => !fileValueMap.has(f.name));
  
  const formatValue = (val: any): string => {
    if (val === null || val === undefined) return 'Not extracted';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (val instanceof Date) return val.toLocaleDateString();
    return String(val);
  };
  
  const formatValueForInput = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (val instanceof Date) return val.toISOString().split('T')[0];
    if (Array.isArray(val)) return val.map(item => String(item ?? '').trim()).filter(Boolean).join('\n');
    return String(val);
  };
  
  const handleFileSelect = (fileName: string) => {
    const fileData = fileValueMap.get(fileName);
    if (fileData) {
      setValue(fileData.value);
      setSelectedFile(fileName);
    } else {
      setSelectedFile(fileName);
      // If no value extracted from this file, keep current value or clear it
      if (!value) {
        setValue('');
      }
    }
  };
  
  const handleSave = async () => {
    if (!value && value !== 0 && value !== false) {
      // Allow empty values only if field is not required
      if (field.fieldDef.required) {
        alert('This field is required. Please enter a value.');
        return;
      }
    }
    if (field.fieldDef.type === 'list' && field.fieldDef.required && Array.isArray(value) && value.length === 0) {
      alert('This field is required. Please add at least one item.');
      return;
    }
    
    setIsSaving(true);
    try {
      // Convert value based on field type
      let processedValue: any = value;
      if (field.fieldDef.type === 'boolean') {
        processedValue = value === 'Yes' || value === 'yes' || value === true || value === 'true' || value === 'True';
      } else if (field.fieldDef.type === 'number') {
        processedValue = value === '' ? null : Number(value);
      } else if (field.fieldDef.type === 'list') {
        if (Array.isArray(value)) {
          processedValue = value.map(item => String(item ?? '').trim()).filter(Boolean);
        } else if (typeof value === 'string') {
          processedValue = value.split(/\n/).map(s => s.trim()).filter(Boolean);
        } else if (value === null || value === undefined || value === '') {
          processedValue = [];
        } else {
          processedValue = [];
        }
      } else if (field.fieldDef.type === 'date') {
        if (!value) {
          processedValue = null;
        } else if (value instanceof Date) {
          // Check if date is valid
          if (isNaN(value.getTime())) {
            console.warn('[FieldEditModal] Invalid date object, setting to null');
            processedValue = null;
          } else {
            processedValue = value;
          }
        } else if (typeof value === 'string') {
          if (value.trim() === '') {
            processedValue = null;
          } else {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              console.warn('[FieldEditModal] Invalid date string:', value, 'setting to null');
              processedValue = null;
            } else {
              processedValue = date;
            }
          }
        } else {
          console.warn('[FieldEditModal] Unexpected date value type:', typeof value, value);
          processedValue = null;
        }
        
        console.log('[FieldEditModal] Processed date value:', {
          original: value,
          processed: processedValue,
          type: typeof processedValue,
          isValid: processedValue instanceof Date ? !isNaN(processedValue.getTime()) : 'N/A'
        });
      } else {
        processedValue = value || null;
      }
      
      console.log('[FieldEditModal] Saving field:', {
        fieldKey: field.fieldKey,
        value: processedValue,
        selectedFile,
        originalValue: value
      });
      
      await onSave(processedValue, selectedFile);
    } catch (error) {
      console.error('[FieldEditModal] Error saving field value:', error);
      alert(`Failed to save field value: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };
  
  const confidenceColors = {
    high: 'bg-green-100 text-green-800 border-green-300',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    low: 'bg-gray-100 text-gray-800 border-gray-300'
  };
  
  return (
    <div className="p-6 space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Field Value {field.fieldDef.required && <span className="text-red-600">*</span>}
          </label>
          {isManuallyUpdated && (
            <div className="flex items-center gap-2 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
              <span>✓ Manually Updated</span>
              {overrideReason && (
                <span className="text-purple-600 font-normal">
                  by {extractUserFromReason(overrideReason)}
                </span>
              )}
              {overrideAt && formatOverrideDate(overrideAt) && (
                <span className="text-purple-500 font-normal">
                  on {formatOverrideDate(overrideAt)}
                </span>
              )}
            </div>
          )}
        </div>
        {field.fieldDef.type === 'boolean' ? (
          <select
            value={formatValueForInput(value)}
            onChange={(e) => setValue(e.target.value === 'Yes')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        ) : field.fieldDef.type === 'date' ? (
          <input
            type="date"
            value={(() => {
              if (!value) return '';
              if (value instanceof Date) {
                // Check if date is valid
                if (isNaN(value.getTime())) return '';
                return value.toISOString().split('T')[0];
              }
              // If it's a string, try to parse it
              if (typeof value === 'string') {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  return date.toISOString().split('T')[0];
                }
              }
              return '';
            })()}
            onChange={(e) => {
              const dateStr = e.target.value;
              if (dateStr) {
                const date = new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
                if (!isNaN(date.getTime())) {
                  setValue(date);
                } else {
                  setValue(null);
                }
              } else {
                setValue(null);
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        ) : field.fieldDef.type === 'number' ? (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => setValue(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        ) : field.fieldDef.type === 'list' ? (
          <textarea
            value={formatValueForInput(value)}
            onChange={(e) => {
              const text = e.target.value;
              setValue(text ? text.split(/\n/).map(s => s.trim()).filter(Boolean) : []);
            }}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="One item per line (e.g. guarantor names)"
          />
        ) : (
          <input
            type="text"
            value={formatValueForInput(value)}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter value manually"
          />
        )}
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Select File and Value ({filteredFiles.length} of {availableFiles.length} files)
          </label>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showOnlyWithValues}
                onChange={(e) => setShowOnlyWithValues(e.target.checked)}
                className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>Show only files with values</span>
            </label>
          </div>
        </div>
        
        {/* Search input */}
        <div className="mb-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search files..."
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        
        <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
          {filteredFiles.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">
              {searchQuery ? 'No files match your search' : 'No files available'}
            </div>
          ) : (
            <div>
              {/* Files with values */}
              {filesWithValues.length > 0 && (
                <div>
                  <div className="sticky top-0 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 border-b border-gray-200">
                    Files with Extracted Values ({filesWithValues.length})
                  </div>
                  <div className="divide-y divide-gray-200">
                    {filesWithValues.map((file) => {
                      const fileData = fileValueMap.get(file.name);
                      const isSelected = selectedFile === file.name;
                      
                      return (
                        <button
                          key={file.id}
                          onClick={() => handleFileSelect(file.name)}
                          className={`w-full p-2 text-left hover:bg-gray-50 transition-colors ${
                            isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-shrink-0 mt-0.5">
                              {isSelected ? (
                                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                              ) : (
                                <FileText className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-900 truncate mb-0.5">
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-600 truncate mb-1">
                                {formatValue(fileData?.value)}
                              </div>
                              <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${
                                confidenceColors[fileData?.confidence as keyof typeof confidenceColors] || confidenceColors.low
                              }`}>
                                {fileData?.confidence || 'low'} confidence
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Files without values */}
              {filesWithoutValues.length > 0 && !showOnlyWithValues && (
                <div>
                  {filesWithValues.length > 0 && (
                    <div className="sticky top-0 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 border-t border-b border-gray-200">
                      Files without Extracted Values ({filesWithoutValues.length})
                    </div>
                  )}
                  <div className="divide-y divide-gray-200">
                    {filesWithoutValues.map((file) => {
                      const isSelected = selectedFile === file.name;
                      
                      return (
                        <button
                          key={file.id}
                          onClick={() => handleFileSelect(file.name)}
                          className={`w-full p-2 text-left hover:bg-gray-50 transition-colors ${
                            isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0">
                              {isSelected ? (
                                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                              ) : (
                                <FileText className="w-4 h-4 text-gray-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-500 truncate">
                                {file.name}
                              </div>
                              <div className="text-[10px] text-gray-400 italic mt-0.5">
                                No value extracted
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Click on a file to select it and use its extracted value. You can also manually edit the value above.
        </p>
      </div>
      
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

// FieldValueCard component for displaying and changing field values
interface FieldValueCardProps {
  field: {
    key: string;
    label: string;
    value: any;
    sourceFile: string;
    confidence: string;
    extractedValues?: any[];
  };
  onValueChange: (newValue: any, newFile: string) => Promise<void>;
}

const FieldValueCard: React.FC<FieldValueCardProps> = ({ field, onValueChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  
  const confidenceColors = {
    high: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-gray-100 text-gray-800'
  };
  
  const formatValue = (val: any): string => {
    if (val === null || val === undefined) return 'N/A';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (val instanceof Date) return val.toLocaleDateString();
    return String(val);
  };
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-900">{field.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded ${confidenceColors[field.confidence as keyof typeof confidenceColors] || confidenceColors.low}`}>
              {field.confidence || 'low'} confidence
            </span>
          </div>
          <div className="text-sm text-gray-700 mt-1">
            <span className="font-medium">Value:</span> {formatValue(field.value)}
          </div>
          {field.sourceFile && (
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <FileText className="w-3 h-3" />
              <span>Source: {field.sourceFile}</span>
            </div>
          )}
        </div>
        {field.extractedValues && field.extractedValues.length > 1 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
        )}
      </div>
      
      {isExpanded && field.extractedValues && field.extractedValues.length > 1 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="text-xs font-medium text-gray-700 mb-2">
            Available values from different files:
          </div>
          <div className="space-y-2">
            {field.extractedValues.map((extracted, idx) => {
              const isCurrent = extracted.fileName === field.sourceFile && extracted.value === field.value;
              return (
                <div
                  key={idx}
                  className={`p-2 rounded border ${
                    isCurrent
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {formatValue(extracted.value)}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        <span>{extracted.fileName}</span>
                        <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${
                          confidenceColors[extracted.confidence as keyof typeof confidenceColors] || confidenceColors.low
                        }`}>
                          {extracted.confidence || 'low'}
                        </span>
                      </div>
                    </div>
                    {!isCurrent && (
                      <button
                        onClick={async () => {
                          setIsChanging(true);
                          try {
                            await onValueChange(extracted.value, extracted.fileName);
                            setIsExpanded(false);
                          } catch (error) {
                            console.error('Error changing field value:', error);
                          } finally {
                            setIsChanging(false);
                          }
                        }}
                        disabled={isChanging}
                        className="ml-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isChanging ? 'Changing...' : 'Use This'}
                      </button>
                    )}
                    {isCurrent && (
                      <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Current
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

type OrgUserRow = {
  id: string;
  email: string;
  name?: string;
  roles?: string[];
  role?: string;
  locationIds?: string[];
};

/** Who can complete (explicit assignees or owner role + users at this location) — same rules as Task Manager */
function getComplianceScoreTaskOwnerDisplay(
  task: TaskWithDetails | undefined,
  orgUsers: OrgUserRow[],
  organizationId: string | undefined
): { title: string; names: string[] } {
  if (!task) {
    return { title: 'Responsible', names: ['—'] };
  }
  const assignedUserIds =
    task.assignedUserIds && Array.isArray(task.assignedUserIds) && task.assignedUserIds.length > 0
      ? task.assignedUserIds
      : task.assignedUserId
        ? [task.assignedUserId]
        : [];

  if (assignedUserIds.length > 0) {
    const picked = orgUsers.filter((u) => assignedUserIds.includes(u.id));
    const names = picked.map((u) => (u.name && u.name.trim()) || u.email || u.id).filter(Boolean);
    return {
      title: 'Assigned to complete',
      names: names.length > 0 ? names : ['No matching users in directory'],
    };
  }

  const roleName = task.templateSnapshot?.owner_role || task.ownerRole;
  if (!organizationId || !roleName) {
    return {
      title: 'Owner role',
      names: roleName ? [roleName] : ['Not set on template'],
    };
  }

  const responsible = orgUsers.filter((user) => {
    const userOwnerRoles = getUserOwnerRolesForOrganization(user, organizationId);
    const hasOwnerRole = userOwnerRoles.includes(roleName);
    const userLocationIds = user.locationIds || [];
    const hasLocationAccess = !!(task.locationId && userLocationIds.includes(task.locationId));
    return hasOwnerRole && hasLocationAccess;
  });

  const names = responsible
    .map((u) => (u.name && u.name.trim()) || u.email || u.id)
    .filter(Boolean);
  return {
    title: `Owner role: ${roleName}`,
    names: names.length > 0 ? names : ['No users with this role at this location'],
  };
}

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { previewState, isPreviewMode } = usePreview();
  const orgTypeConfig = useOrganizationType();
  const { subscription, hasActiveSubscription, refreshSubscription } = useSubscription();
  const { showNotification } = useNotification();
  const { selectedLocationIds, hasActiveFilters, availableLocations } = useLocationFilter();
  const navigate = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTasks, setFilteredTasks] = useState<TaskWithDetails[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [showHighRiskModal, setShowHighRiskModal] = useState(false);
  const [showPendingReviewModal, setShowPendingReviewModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskWithDetails | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectedTask, setRejectedTask] = useState<TaskWithDetails | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [locationScores, setLocationScores] = useState<Map<string, number>>(new Map());
  const [locationScoreDetails, setLocationScoreDetails] = useState<Map<string, LocationScore>>(new Map());
  /** All tasks at the user’s locations (before owner_role filter), used for one canonical compliance score + scorecard details */
  const [tasksForComplianceScorecard, setTasksForComplianceScorecard] = useState<TaskWithDetails[]>([]);
  const [selectedLocationForDetails, setSelectedLocationForDetails] = useState<string | null>(null);
  const [taskFilterLocation, setTaskFilterLocation] = useState<string>('all');
  const [taskFilterRiskLevel, setTaskFilterRiskLevel] = useState<string>('all');
  const [taskSortBy, setTaskSortBy] = useState<'task' | 'location' | 'category' | 'due' | 'status' | 'risk'>('due');
  const [taskSortOrder, setTaskSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isScorecardExpanded, setIsScorecardExpanded] = useState<boolean>(false);
  const [isSiteMetricsExpanded, setIsSiteMetricsExpanded] = useState<boolean>(false);
  const [showScorecardModal, setShowScorecardModal] = useState<boolean>(false);
  const [showSiteMetricsModal, setShowSiteMetricsModal] = useState<boolean>(false);
  const [userOwnerRoles, setUserOwnerRoles] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  const [isUserManager, setIsUserManager] = useState<boolean>(false);
  const [userTaskIds, setUserTaskIds] = useState<Set<string>>(new Set());
  const [filterMyTasks, setFilterMyTasks] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState<Array<{ id: string; email: string; name?: string; roles?: string[]; role?: string; locationIds?: string[] }>>([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(true);
  const [showNotificationBanner, setShowNotificationBanner] = useState<boolean>(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetCompletions, setAssetCompletions] = useState<Record<string, ChecklistCompletion[]>>({});
  const [isLoadingComplianceReview, setIsLoadingComplianceReview] = useState<boolean>(false);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [assetNameFilter, setAssetNameFilter] = useState<string>('');
  const [assetTypeFilter, setAssetTypeFilter] = useState<string>('all');
  const [loanOfficerFilter, setLoanOfficerFilter] = useState<string>('all');
  const [eligibleLoanOfficers, setEligibleLoanOfficers] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const [expandedAssets, setExpandedAssets] = useState<Set<string>>(new Set());
  const [selectedAssetForDetails, setSelectedAssetForDetails] = useState<Asset | null>(null);
  const [selectedCompletionForDetails, setSelectedCompletionForDetails] = useState<ChecklistCompletion | null>(null);
  const [checklistDetails, setChecklistDetails] = useState<Checklist | null>(null);
  const [evidenceList, setEvidenceList] = useState<ChecklistEvidence[]>([]);
  const [assetFacts, setAssetFacts] = useState<Record<string, any>>({});
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [expandedChecklistItems, setExpandedChecklistItems] = useState<Set<string>>(new Set());
  const [itemEvidenceMap, setItemEvidenceMap] = useState<Record<string, ChecklistEvidence[]>>({});
  const [editingField, setEditingField] = useState<{itemText: string; fieldKey: string; fieldDef: any} | null>(null);
  const [availableFiles, setAvailableFiles] = useState<Array<{id: string; name: string; url?: string}>>([]);
  const [fileUrlMap, setFileUrlMap] = useState<Record<string, string>>({});
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [reevaluatingItems, setReevaluatingItems] = useState<Set<string>>(new Set());
  const [markingAsPassedItems, setMarkingAsPassedItems] = useState<Set<string>>(new Set());
  /** Bank checklist run details: show only actionable items/fields */
  const [checklistRunDetailsViewMode, setChecklistRunDetailsViewMode] = useState<'all' | 'actionable'>('all');
  // Reference image viewer state
  const [viewingImageUrl, setViewingImageUrl] = useState<string | null>(null);
  const [viewingImageIndex, setViewingImageIndex] = useState<number>(0);
  const [viewingImageUrls, setViewingImageUrls] = useState<string[]>([]);
  const [viewingTask, setViewingTask] = useState<TaskWithDetails | null>(null);

  useEffect(() => {
    const initialize = async () => {
      // If user has no organization, redirect to organizations page
      if (user?.id && !currentOrganization?.id) {
        // Check if user actually has organizationId in their profile
        const { db } = await import('@/firebase/config');
        const { doc, getDoc } = await import('firebase/firestore');
        const userRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (!userData?.organizationId) {
            // User doesn't have organization - redirect to create one
            navigate('/organizations?newSignup=true');
            return;
          }
        }
      }
      
      // Check if we're coming from subscription success (via URL params or sessionStorage)
      const searchParams = new URLSearchParams(location.search);
      const sessionId = searchParams.get('session_id');
      const fromSubscriptionSuccess = sessionStorage.getItem('fromSubscriptionSuccess');
      
      if (sessionId || fromSubscriptionSuccess) {
        // Refresh subscription when coming from Stripe redirect
        console.log('Refreshing subscription after Stripe redirect');
        await refreshSubscription();
        // Clear the flag
        sessionStorage.removeItem('fromSubscriptionSuccess');
        // Remove session_id from URL if present
        if (sessionId) {
          navigate('/home', { replace: true });
        }
      }
      
      // Load user role and owner roles first, then load dashboard data (which depends on user role)
      if (user?.id) {
        await loadUserRole();
        await loadUserRoles();
      }
      await loadDashboardData();
      
      // Initialize FCM after page loads (retry if interrupted during login/navigation)
      // This ensures FCM initializes even if permission request was interrupted by redirect
      try {
        const { initializeFCM } = await import('@/services/fcmService');
        await initializeFCM();
        
        // Check if notification permission needs to be requested
        // Show banner if permission is "default" (not yet requested)
        if ('Notification' in window && Notification.permission === 'default') {
          setShowNotificationBanner(true);
        }
      } catch (error) {
        // Silently fail - FCM initialization is non-critical
        console.warn('⚠️ FCM initialization failed on HomePage:', error);
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentOrganization?.id,
    user?.id,
    location.search,
    isPreviewMode,
    previewState?.previewUserId,
    previewState?.previewType,
  ]);

  // Keyboard navigation for image viewer
  useEffect(() => {
    if (!viewingImageUrl) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setViewingImageUrl(null);
        setViewingImageUrls([]);
        setViewingImageIndex(0);
        setViewingTask(null);
      } else if (e.key === 'ArrowLeft' && viewingImageIndex > 0) {
        const newIndex = viewingImageIndex - 1;
        setViewingImageIndex(newIndex);
        setViewingImageUrl(viewingImageUrls[newIndex]);
      } else if (e.key === 'ArrowRight' && viewingImageIndex < viewingImageUrls.length - 1) {
        const newIndex = viewingImageIndex + 1;
        setViewingImageIndex(newIndex);
        setViewingImageUrl(viewingImageUrls[newIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewingImageUrl, viewingImageIndex, viewingImageUrls]);

  const loadUserRole = async () => {
    if (user?.id) {
      const role = await getUserRole(user.id);
      setUserRole(role);
    }
  };

  const loadUserRoles = async () => {
    if (user?.id && currentOrganization?.id) {
      try {
        const { db } = await import('@/firebase/config');
        const { doc, getDoc } = await import('firebase/firestore');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          
          // Use organization-specific role helpers
          const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
          const isSystemUserRole = isSystemUserInOrganization(userData, organizationId);
          const isUserManagerRole = isManagerInOrganization(userData, organizationId) || isAdminInOrganization(userData, organizationId);
          
          setUserOwnerRoles(ownerRoles);
          setIsSystemUser(isSystemUserRole);
          setIsUserManager(isUserManagerRole);
        } else {
          // Fallback to legacy fetchUserRoles if user not found
          const roles = await fetchUserRoles(user.id);
          setUserOwnerRoles(roles.ownerRoles);
          setIsSystemUser(roles.isSystemUser);
          setIsUserManager(roles.isSystemManager);
        }
      } catch (error) {
        console.error('Error loading user roles:', error);
        // Fallback to legacy fetchUserRoles on error
        const roles = await fetchUserRoles(user.id);
        setUserOwnerRoles(roles.ownerRoles);
        setIsSystemUser(roles.isSystemUser);
        setIsUserManager(roles.isSystemManager);
      }
    } else if (user?.id) {
      // Fallback: use global roles if no organization
      const roles = await fetchUserRoles(user.id);
      setUserOwnerRoles(roles.ownerRoles);
      setIsSystemUser(roles.isSystemUser);
      setIsUserManager(roles.isSystemManager);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [tasks, searchQuery, taskFilterLocation, taskFilterRiskLevel, taskSortBy, taskSortOrder, filterMyTasks, userTaskIds, selectedLocationIds, hasActiveFilters]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'pending_review':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCompleteTask = (task: TaskWithDetails) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to complete tasks.', 'error');
      navigate('/subscription');
      return;
    }

    setSelectedTask(task);
    setShowCompleteModal(true);

    // Allow modal to render and then focus the first actionable element
    setTimeout(() => {
      if (document && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      const modal = document.querySelector('[data-testid="complete-task-modal"]') as HTMLElement | null;
      if (modal) {
        modal.focus?.();
        const primaryButton = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement | null;
        primaryButton?.focus?.();
      }
    }, 40);
  };

  const handleReviewTask = (task: TaskWithDetails) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to review tasks.', 'error');
      navigate('/subscription');
      return;
    }

    if (task.status !== 'pending_review') {
      console.warn(`Task with id ${task.id} is not pending review`);
      return;
    }
    setSelectedTask(task);
    setShowReviewModal(true);
  };

  const handleCompleteFromScorecard = (taskId: string) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to complete tasks.', 'error');
      navigate('/subscription');
      return;
    }

    const taskToComplete =
      tasks.find((t) => t.id === taskId) ??
      tasksForComplianceScorecard.find((t) => t.id === taskId);
    if (taskToComplete) {
      handleCompleteTask(taskToComplete);
    } else {
      console.warn(`Task with id ${taskId} not found for completion from scorecard`);
    }
  };

  const handleReviewFromScorecard = (taskId: string) => {
    // Check if subscription is cancelled
    if (subscription?.status === 'canceled') {
      showNotification('Your subscription has been cancelled. Please renew your subscription to review tasks.', 'error');
      navigate('/subscription');
      return;
    }

    const taskToReview =
      tasks.find((t) => t.id === taskId) ??
      tasksForComplianceScorecard.find((t) => t.id === taskId);
    if (taskToReview) {
      if (taskToReview.status !== 'pending_review') {
        console.warn(`Task with id ${taskId} is not pending review`);
        return;
      }
      setSelectedTask(taskToReview);
      setShowReviewModal(true);
    } else {
      console.warn(`Task with id ${taskId} not found for review from scorecard`);
    }
  };

  const handleReviewComplete = async () => {
    // Reload tasks after review
    await loadDashboardData();
    setShowReviewModal(false);
  };

  const loadDashboardData = async () => {
    // Don't load tasks if user doesn't have an organization
    if (!currentOrganization?.id) {
      setTasks([]);
      setTasksForComplianceScorecard([]);
      setIsLoadingMetrics(false);
      return;
    }

    setIsLoadingMetrics(true);
    try {
      // Load all users for the organization to match by owner role
      if (currentOrganization?.id) {
        try {
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const usersRef = collection(db, 'users');
          const organizationId = currentOrganization.id;
          
          // Query 1: Users with organizationId matching current organization (legacy support)
          const orgIdQuery = query(usersRef, where('organizationId', '==', organizationId));
          
          // Query 2: Users with organizationIds array containing current organization
          const orgIdsQuery = query(usersRef, where('organizationIds', 'array-contains', organizationId));
          
          // Execute both queries in parallel
          const [orgIdSnapshot, orgIdsSnapshot] = await Promise.all([
            getDocs(orgIdQuery),
            getDocs(orgIdsQuery)
          ]);
          
          // Combine results and deduplicate by user ID
          const usersMap = new Map<string, { id: string; email: string; name?: string; roles?: string[]; role?: string; locationIds?: string[] }>();
          
          // Process users from organizationId query
          orgIdSnapshot.forEach((userDoc) => {
            const userData = userDoc.data();
            // Use organization-specific roles if available
            const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
            const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
            const allOrgRoles = systemRole ? [systemRole, ...ownerRoles] : ownerRoles;
            
            usersMap.set(userDoc.id, {
              id: userDoc.id,
              email: userData.email || '',
              name: userData.name || userData.displayName || '',
              roles: allOrgRoles.length > 0 ? allOrgRoles : (userData.roles || (userData.role ? [userData.role] : [])), // Fallback to legacy if no org roles
              role: systemRole || userData.role, // Fallback to legacy if no org role
              locationIds: userData.locationIds || []
            });
          });
          
          // Process users from organizationIds array query (may include duplicates)
          orgIdsSnapshot.forEach((userDoc) => {
            // Skip if already added from first query
            if (usersMap.has(userDoc.id)) return;
            
            const userData = userDoc.data();
            // Use organization-specific roles if available
            const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
            const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
            const allOrgRoles = systemRole ? [systemRole, ...ownerRoles] : ownerRoles;
            
            usersMap.set(userDoc.id, {
              id: userDoc.id,
              email: userData.email || '',
              name: userData.name || userData.displayName || '',
              roles: allOrgRoles.length > 0 ? allOrgRoles : (userData.roles || (userData.role ? [userData.role] : [])), // Fallback to legacy if no org roles
              role: systemRole || userData.role, // Fallback to legacy if no org role
              locationIds: userData.locationIds || []
            });
          });
          
          // Convert map to array
          const usersList = Array.from(usersMap.values());
          setAllUsers(usersList);
        } catch (error) {
          console.error('Error loading users:', error);
        }
      }

      // Load all locations first to filter out tasks from deleted locations
      const locationsResult = await locationService.getAllLocations(currentOrganization.id);
      const validLocationIds = new Set<string>();
      if (locationsResult.success && locationsResult.data) {
        locationsResult.data.forEach(loc => {
          if (loc.id && !loc.deleted) {
            validLocationIds.add(loc.id);
          }
        });
      }

      // Get tasks from the tasks table (already filtered by organization on server side)
      let organizationTasks = await getTasksForOrganization(currentOrganization.id);
      
      // Filter out tasks from deleted locations
      organizationTasks = organizationTasks.filter(task => {
        if (!task.locationId) return false;
        return validLocationIds.has(task.locationId);
      });
      
      // Initialize currentUserOwnerRoles for use later (for "My Tasks" filter)
      let currentUserOwnerRoles: string[] = [];
      /** Tasks after location (and preview) filters but before owner_role filter — used for org-wide compliance score */
      let tasksBeforeOwnerRoleFilter: TaskWithDetails[] = [];
      
      // In preview mode, use preview user's data if previewing by user, otherwise show all
      // Otherwise, filter by user's accessible locations and roles
      if (isPreviewMode && previewState?.previewType === 'user' && previewState.previewUserId) {
        // Preview as specific user: use their locationIds and roles
        console.log('[HomePage] Preview mode: previewing as user', previewState.previewUserId);
        
        // Use preview user's locationIds
        if (previewState.locationIds && previewState.locationIds.length > 0) {
          organizationTasks = organizationTasks.filter(task => 
            task.locationId && previewState.locationIds!.includes(task.locationId)
          );
        } else {
          // Preview user has no assigned locations - show no tasks
          organizationTasks = [];
        }
        tasksBeforeOwnerRoleFilter = [...organizationTasks];
        
        // Fetch preview user's roles to filter by owner_role
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const previewUserRef = doc(db, 'users', previewState.previewUserId);
          const previewUserSnap = await getDoc(previewUserRef);
          
          if (previewUserSnap.exists()) {
            const previewUserData = previewUserSnap.data();
            const organizationId = currentOrganization?.id;
            
            let isSystemUserRole = false;
            
            // Use organization-specific roles if organization is available
            if (organizationId) {
              currentUserOwnerRoles = getUserOwnerRolesForOrganization(previewUserData, organizationId);
              isSystemUserRole = isSystemUserInOrganization(previewUserData, organizationId);
            } else {
              // Fallback to global roles if no organization
              const previewUserRoles = normalizeRoles(previewUserData?.roles || previewUserData?.role);
              const SYSTEM_ROLES_LIST = ['user', 'manager', 'admin'];
              currentUserOwnerRoles = previewUserRoles.filter((r: string) => !SYSTEM_ROLES_LIST.includes(r.toLowerCase()));
              isSystemUserRole = previewUserRoles.some((r: string) => r.toLowerCase() === 'user') && 
                                !previewUserRoles.some((r: string) => ['manager', 'admin'].includes(r.toLowerCase()));
            }

            // Mirror preview user’s permissions in UI state (otherwise logged-in admin roles stay and scorecard shows Complete enabled)
            {
              const orgId = currentOrganization?.id;
              if (orgId) {
                const systemRole = getUserSystemRoleForOrganization(previewUserData, orgId);
                setUserOwnerRoles(currentUserOwnerRoles);
                setIsSystemUser(isSystemUserRole);
                setIsUserManager(systemRole === 'manager' || systemRole === 'admin');
              } else {
                const allRoles = normalizeRoles(previewUserData?.roles || previewUserData?.role);
                setUserOwnerRoles(currentUserOwnerRoles);
                setIsSystemUser(isSystemUserRole);
                setIsUserManager(
                  allRoles.some((r: string) => ['manager', 'admin'].includes(r.toLowerCase()))
                );
              }
            }
            
            // Filter tasks by owner_role for system "User" role users
            if (isSystemUserRole && currentUserOwnerRoles.length > 0) {
              organizationTasks = organizationTasks.filter(task => {
                const taskOwnerRole = task.templateSnapshot?.owner_role || task.ownerRole;
                if (!taskOwnerRole) {
                  return false;
                }
                const organizationType = currentOrganization?.industryType || null;
            return taskMatchesUserOwnerRoles(taskOwnerRole, currentUserOwnerRoles, organizationType);
              });
            }
          }
        } catch (error) {
          console.error('Error fetching preview user roles:', error);
        }
      } else if (!isPreviewMode) {
        // Filter tasks by user's accessible locations (always applies, regardless of system role)
        if (user?.id) {
          // Fetch user's full data from Firestore to get locationIds
          let userLocationIds: string[] | undefined;
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('@/firebase/config');
            const userRef = doc(db, 'users', user.id);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              userLocationIds = userData.locationIds;
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
          
          // Filter tasks to only include those at user's assigned locations
          if (userLocationIds && userLocationIds.length > 0) {
            organizationTasks = organizationTasks.filter(task => 
              task.locationId && userLocationIds!.includes(task.locationId)
            );
          } else {
            // User has no assigned locations - show no tasks
            organizationTasks = [];
          }
        }
        tasksBeforeOwnerRoleFilter = [...organizationTasks];
        
        // Fetch user's roles for the current organization to determine if we need to filter by owner_role
        let isSystemUserRole = false;
        if (user?.id && currentOrganization?.id) {
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('@/firebase/config');
            const userRef = doc(db, 'users', user.id);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
              const userData = userSnap.data();
              const organizationId = currentOrganization.id;
              
              // Get organization-specific roles
              const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
              const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
              
              // Check if user is a system "user" role (not manager or admin)
              isSystemUserRole = isSystemUserInOrganization(userData, organizationId);
              
              currentUserOwnerRoles = ownerRoles;
              
              // Update state for other uses
              setUserOwnerRoles(ownerRoles);
              setIsSystemUser(isSystemUserRole);
              setIsUserManager(systemRole === 'manager' || systemRole === 'admin');
              
              console.log(`[HomePage] User roles for org ${organizationId}: systemRole=${systemRole}, ownerRoles=`, ownerRoles, `isSystemUser=${isSystemUserRole}`);
            } else {
              // Fallback to legacy fetchUserRoles if user document doesn't exist
              const roles = await fetchUserRoles(user.id);
              currentUserOwnerRoles = roles.ownerRoles;
              isSystemUserRole = roles.isSystemUser;
              setUserOwnerRoles(roles.ownerRoles);
              setIsSystemUser(roles.isSystemUser);
              setIsUserManager(roles.isSystemManager);
            }
          } catch (error) {
            console.error('Error fetching user roles in loadDashboardData:', error);
            // Fallback to state if fetch fails
            currentUserOwnerRoles = userOwnerRoles;
            // Check if user is system user from state
            isSystemUserRole = isSystemUser;
          }
        }
        
        // Filter tasks by owner_role for system "User" role users
        if (isSystemUserRole && currentUserOwnerRoles.length > 0) {
          organizationTasks = organizationTasks.filter(task => {
            const taskOwnerRole = task.templateSnapshot?.owner_role || task.ownerRole;
            if (!taskOwnerRole) {
              // Tasks without owner_role should not be shown to system users
              console.log(`[HomePage] Task "${task.templateName}" excluded: no owner_role`);
              return false;
            }
            const organizationType = currentOrganization?.industryType || null;
            const matches = taskMatchesUserOwnerRoles(taskOwnerRole, currentUserOwnerRoles, organizationType);
            if (!matches) {
              console.log(`[HomePage] Task "${task.templateName}" excluded: owner_role="${taskOwnerRole}" doesn't match user roles:`, currentUserOwnerRoles);
            }
            return matches;
          });
        }
      } else {
        // In preview mode: show all tasks for the organization (admin view)
        // Don't filter by locationIds or owner roles
        // For "My Tasks" filter, we'll use empty array (no tasks will match, which is fine for preview)
        console.log('[HomePage] Preview mode: showing all tasks for organization');
        currentUserOwnerRoles = []; // Empty array so "My Tasks" filter won't match anything in preview mode
        tasksBeforeOwnerRoleFilter = [...organizationTasks];
      }
      
      // Load completion details for completed tasks (full location set for score; visible subset for dashboard)
      const tasksWithCompletionsForScore = await Promise.all(
        tasksBeforeOwnerRoleFilter.map(async (task) => {
          if (task.status === 'completed') {
            const completionDetails = await getTaskCompletionDetails(task.id);
            return { ...task, completionDetails };
          }
          return task;
        })
      );
      const visibleTaskIds = new Set(organizationTasks.map((t) => t.id));
      const tasksWithCompletions = tasksWithCompletionsForScore.filter((t) => visibleTaskIds.has(t.id));
      
      // Identify which tasks are assigned to user based on owner roles
      // For "My Tasks" filter - track which tasks match user's owner roles
      const assignedTaskIds = new Set<string>();
      if (user?.id) {
        // Track tasks matching user's owner roles (for "My Tasks" filter)
        tasksWithCompletions.forEach(task => {
          const taskOwnerRole = task.templateSnapshot?.owner_role || task.ownerRole;
          const organizationType = currentOrganization?.industryType || null;
          if (taskMatchesUserOwnerRoles(taskOwnerRole, currentUserOwnerRoles, organizationType)) {
            assignedTaskIds.add(task.id);
          }
        });
      }
      
      setUserTaskIds(assignedTaskIds);
      setTasks(tasksWithCompletions);
      setTasksForComplianceScorecard(tasksWithCompletionsForScore);
      
      // Load templates for score calculation
      if (currentOrganization?.id) {
        const templatesResult = await complianceService.getAllTemplates(currentOrganization.id);
        if (templatesResult.success && templatesResult.data) {
          setTemplates(templatesResult.data);
          
          // Calculate location scores from all tasks at assigned locations (canonical score for every team member)
          const scores = new Map<string, number>();
          const locationMap = new Map<string, TaskWithDetails[]>();
          
          tasksWithCompletionsForScore.forEach(task => {
            if (!locationMap.has(task.locationId)) {
              locationMap.set(task.locationId, []);
            }
            locationMap.get(task.locationId)!.push(task);
          });
          
          const detailsMap = new Map<string, LocationScore>();
          
          locationMap.forEach((locationTasks, locationId) => {
            const locationName = locationTasks[0]?.locationName || '';
            const locationScore = calculateLocationScore(locationId, locationName, locationTasks, templatesResult.data || []);
            scores.set(locationName, locationScore.score);
            // Store full score details for the detail panel
            detailsMap.set(locationName, locationScore);
          });
          
          setLocationScores(scores);
          setLocationScoreDetails(detailsMap);
        }
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  // Load compliance review data (assets and checklist completions)
  const loadComplianceReviewData = async () => {
    if (!currentOrganization?.id || !orgTypeConfig.homePage.showComplianceReview) {
      return;
    }

    setIsLoadingComplianceReview(true);
    try {
      // Fetch all assets for the organization
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('@/firebase/config');
      const assetsRef = collection(db, 'assets');
      const assetsQuery = query(
        assetsRef,
        where('organizationId', '==', currentOrganization.id)
      );
      const assetsSnapshot = await getDocs(assetsQuery);
      const assetsList: Asset[] = [];
      assetsSnapshot.forEach((doc) => {
        assetsList.push({ id: doc.id, ...doc.data() } as Asset);
      });
      setAssets(assetsList);

      // Load asset types for filtering
      const assetTypesResult = await assetService.getAllAssetTypes(currentOrganization.id);
      if (assetTypesResult.success && assetTypesResult.data) {
        setAssetTypes(assetTypesResult.data);
        
        // Find loan officer field in asset types and load eligible loan officers
        const loanOfficerField = assetTypesResult.data
          .flatMap(at => at.customFields || [])
          .find(cf => cf.type === 'loan_officer_picker');
        
        if (loanOfficerField && loanOfficerField.metadata?.roleFilter) {
          // Load users with Loan Officer role
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const usersRef = collection(db, 'users');
          const orgIdQuery = query(usersRef, where('organizationId', '==', currentOrganization.id));
          const orgIdsQuery = query(usersRef, where('organizationIds', 'array-contains', currentOrganization.id));
          
          const [orgIdSnapshot, orgIdsSnapshot] = await Promise.all([
            getDocs(orgIdQuery),
            getDocs(orgIdsQuery)
          ]);
          
          const allUserDocs = new Map<string, any>();
          orgIdSnapshot.forEach(doc => {
            allUserDocs.set(doc.id, { id: doc.id, ...doc.data() });
          });
          orgIdsSnapshot.forEach(doc => {
            allUserDocs.set(doc.id, { id: doc.id, ...doc.data() });
          });
          
          const eligibleUsers: Array<{ id: string; name: string; email?: string }> = [];
          for (const userDoc of allUserDocs.values()) {
            const userOwnerRoles = getUserOwnerRolesForOrganization(userDoc, currentOrganization.id);
            if (userOwnerRoles.includes(loanOfficerField.metadata.roleFilter)) {
              eligibleUsers.push({
                id: userDoc.id,
                name: userDoc.name || userDoc.email || 'Unknown User',
                email: userDoc.email
              });
            }
          }
          eligibleUsers.sort((a, b) => a.name.localeCompare(b.name));
          setEligibleLoanOfficers(eligibleUsers);
        }
      }

      // Fetch checklist completions for each asset
      const completionsMap: Record<string, ChecklistCompletion[]> = {};
      const completionsRef = collection(db, 'checklist_completions');
      
      // Fetch all completions for the organization (filtered by assetId in variableValues)
      const allCompletionsSnapshot = await getDocs(
        query(completionsRef, where('organizationId', '==', currentOrganization.id))
      );
      
      allCompletionsSnapshot.forEach((doc) => {
        const completion = { id: doc.id, ...doc.data() } as ChecklistCompletion;
        // Check if completion is for one of our assets
        const assetId = completion.assetId || completion.variableValues?.assetId || completion.variableValues?.asset;
        if (assetId && assetsList.some(a => a.id === assetId)) {
          if (!completionsMap[assetId]) {
            completionsMap[assetId] = [];
          }
          completionsMap[assetId].push(completion);
        }
      });
      
      setAssetCompletions(completionsMap);
    } catch (error) {
      console.error('Error loading compliance review data:', error);
      showNotification('Failed to load compliance review data', 'error');
    } finally {
      setIsLoadingComplianceReview(false);
    }
  };

  // Load compliance review data when organization changes
  useEffect(() => {
    if (orgTypeConfig.homePage.showComplianceReview) {
      loadComplianceReviewData();
    }
  }, [currentOrganization?.id, orgTypeConfig.homePage.showComplianceReview]);

  // Prefetch evidence for actionable-only view (Bank checklist run details)
  useEffect(() => {
    if (!orgTypeConfig.homePage.showChecklistRunActionableFilter || checklistRunDetailsViewMode !== 'actionable') {
      return;
    }
    const completionId = selectedCompletionForDetails?.id;
    const items = checklistDetails?.items;
    const completion = selectedCompletionForDetails;
    if (!completionId || !items?.length || !completion) return;

    const actionableTexts = items
      .filter((it) => isActionableChecklistItemStatus(completion.itemResults?.[it.text]?.status))
      .map((it) => it.text);
    if (actionableTexts.length === 0) return;

    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        actionableTexts.map(async (text) => {
          const r = await checklistEvidenceService.getEvidenceByItemResult(completionId, text);
          return { text, data: r.success && r.data ? r.data : [] };
        })
      );
      if (cancelled) return;
      setItemEvidenceMap((prev) => {
        const next = { ...prev };
        for (const { text, data } of results) {
          if (!next[text]?.length) {
            next[text] = data;
          }
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [
    checklistRunDetailsViewMode,
    orgTypeConfig.homePage.showChecklistRunActionableFilter,
    selectedCompletionForDetails?.id,
    checklistDetails?.items,
    selectedCompletionForDetails?.itemResults,
  ]);

  // Function to load detailed checklist run information
  const loadChecklistRunDetails = async (asset: Asset, completion: ChecklistCompletion) => {
    setIsLoadingDetails(true);
    setExpandedChecklistItems(new Set()); // Reset expanded items
    setItemEvidenceMap({}); // Reset evidence map
    try {
      // Always reload the latest completion from Firestore to ensure we have the most up-to-date data
      let latestCompletion = completion;
      if (completion.id) {
        const reloadResult = await checklistCompletionService.getCompletion(completion.id);
        if (reloadResult.success && reloadResult.data) {
          latestCompletion = reloadResult.data;
          // Also update the cached completion in assetCompletions
          setAssetCompletions(prev => {
            const updated = { ...prev };
            if (asset.id && updated[asset.id]) {
              const index = updated[asset.id].findIndex(c => c.id === completion.id);
              if (index >= 0) {
                updated[asset.id] = [...updated[asset.id]];
                updated[asset.id][index] = latestCompletion;
              }
            }
            return updated;
          });
        }
      }
      
      // Set the latest completion
      setSelectedCompletionForDetails(latestCompletion);
      
      // Load checklist details
      if (latestCompletion.checklistId) {
        const checklistResult = await checklistService.getChecklist(latestCompletion.checklistId);
        if (checklistResult.success && checklistResult.data) {
          setChecklistDetails(checklistResult.data);
        }
      }

      // Load asset facts for field values
      if (asset.id) {
        const factsResult = await assetFactsService.getAssetFacts(asset.id);
        if (factsResult.success && factsResult.data) {
          const factsRecord: Record<string, any> = {};
          if (factsResult.data.facts) {
            Object.entries(factsResult.data.facts).forEach(([key, fact]) => {
              factsRecord[key] = fact;
            });
          }
          setAssetFacts(factsRecord);
        }
        
        // Load available files for this asset
        if (currentOrganization?.id) {
          setIsLoadingFiles(true);
          try {
            const { assetFileService } = await import('@/firebase/assetFileService');
            const filesResult = await assetFileService.getAssetFiles(asset.id, currentOrganization.id);
            if (filesResult.success && filesResult.data) {
              const files = filesResult.data.map(f => ({ 
                id: f.id || '', 
                name: f.fileName || 'Unknown',
                url: f.storageUrl
              }));
              setAvailableFiles(files);
              
              // Create a map of file name to URL for quick lookup
              const urlMap: Record<string, string> = {};
              files.forEach(file => {
                if (file.url) {
                  urlMap[file.name] = file.url;
                }
              });
              setFileUrlMap(urlMap);
            }
          } catch (error) {
            console.error('Error loading files:', error);
          } finally {
            setIsLoadingFiles(false);
          }
        }
      }

      // Load evidence for this completion (will be loaded on-demand when items are expanded)
      if (latestCompletion.id) {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const evidenceRef = collection(db, 'checklist_evidence');
        const evidenceQuery = query(
          evidenceRef,
          where('checklistCompletionId', '==', latestCompletion.id)
        );
        const evidenceSnapshot = await getDocs(evidenceQuery);
        const evidenceList: ChecklistEvidence[] = [];
        evidenceSnapshot.forEach((doc) => {
          evidenceList.push({ id: doc.id, ...doc.data() } as ChecklistEvidence);
        });
        setEvidenceList(evidenceList);
      }

      // Load asset facts
      if (asset.id) {
        const factsResult = await assetFactsService.getAssetFacts(asset.id);
        if (factsResult.success && factsResult.data) {
          // Convert AssetFacts to Record<string, AssetFact> for easier access
          const factsRecord: Record<string, any> = {};
          if (factsResult.data.facts) {
            Object.entries(factsResult.data.facts).forEach(([key, fact]) => {
              factsRecord[key] = fact;
            });
          }
          setAssetFacts(factsRecord);
        }
      }
    } catch (error) {
      console.error('Error loading checklist run details:', error);
      showNotification('Failed to load checklist run details', 'error');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Function to open side panel with details
  const handleViewDetails = async (asset: Asset, completion: ChecklistCompletion) => {
    setChecklistRunDetailsViewMode('all');
    setSelectedAssetForDetails(asset);
    setSelectedCompletionForDetails(completion);
    await loadChecklistRunDetails(asset, completion);
  };

  // Function to close side panel
  const handleCloseDetails = () => {
    setSelectedAssetForDetails(null);
    setSelectedCompletionForDetails(null);
    setChecklistDetails(null);
    setEvidenceList([]);
    setAssetFacts({});
    setChecklistRunDetailsViewMode('all');
  };

  // Function to re-evaluate a checklist item
  const handleReevaluateItem = async (item: ChecklistItem) => {
    if (!selectedAssetForDetails || !selectedCompletionForDetails || !checklistDetails) {
      showNotification('Missing required data for re-evaluation', 'error');
      return;
    }

    if (!selectedAssetForDetails.id || !currentOrganization?.id || !user?.id) {
      showNotification('Missing required information. Please try again.', 'error');
      return;
    }

    // Set loading state
    setReevaluatingItems(prev => new Set(prev).add(item.text));

    try {
      console.log('[HomePage] Re-evaluating item:', item.text);

      // 1. Gather current facts using existing function
      const extractedFields = extractEvidenceFieldsForItem(item, assetFacts);
      console.log('[HomePage] Extracted fields for re-evaluation:', extractedFields);

      // 1.1. Clear workflow-derived field values in asset facts before re-evaluate so old values (e.g. OTHER) don't persist
      const workflowFieldKeys = new Set<string>();
      (item.evidenceDefinitions ?? []).forEach(evDef => {
        (evDef.fieldDefinitions ?? []).forEach(fd => {
          if (fd.workflowRules?.trim()) workflowFieldKeys.add(fd.key);
        });
      });
      if (workflowFieldKeys.size > 0 && selectedAssetForDetails?.id && currentOrganization?.id) {
        const clearPayload: Record<string, { value: null; source: 'EXTRACTED'; extractedFrom: string; confidence?: 'high' }> = {};
        workflowFieldKeys.forEach(k => {
          clearPayload[k] = { value: null, source: 'EXTRACTED', extractedFrom: 'Workflow rule (cleared)', confidence: 'high' };
        });
        const clearResult = await assetFactsService.setFacts(selectedAssetForDetails.id, clearPayload, currentOrganization.id);
        if (clearResult.success) {
          console.log('[HomePage] Cleared workflow-derived keys before re-evaluate:', [...workflowFieldKeys]);
        }
      }

      // 1.2. Persist workflow-derived field values to asset facts so they can be used later and in evidence evaluation
      const workflowFactsToSave: Record<string, { value: any; source: 'EXTRACTED'; extractedFrom?: string; confidence?: 'high' | 'medium' | 'low' }> = {};
      for (const evidenceName of Object.keys(extractedFields)) {
        const fields = extractedFields[evidenceName];
        if (!fields || typeof fields !== 'object') continue;
        for (const [fieldKey, fieldInfo] of Object.entries(fields)) {
          const info = fieldInfo as { value?: any; source?: string };
          if (info?.source === 'Workflow rule' && info?.value !== null && info?.value !== undefined && info?.value !== '') {
            if (!(fieldKey in workflowFactsToSave)) {
              workflowFactsToSave[fieldKey] = {
                value: info.value,
                source: 'EXTRACTED',
                extractedFrom: 'Workflow rule',
                confidence: 'high',
              };
            }
          }
        }
      }

      // Build merged facts (asset facts + existing evidence fieldData) so workflow rules can use fields from other evidences (e.g. collateralCode from Collateral Identification)
      let factsForWorkflowAI: Record<string, any> = { ...assetFacts };
      if (selectedCompletionForDetails?.id) {
        const existingEvidenceResult = await checklistEvidenceService.getEvidenceByItemResult(
          selectedCompletionForDetails.id,
          item.text
        );
        if (existingEvidenceResult.success && existingEvidenceResult.data) {
          for (const ev of existingEvidenceResult.data) {
            const fieldValues = (ev as any).fieldValues;
            if (fieldValues && typeof fieldValues === 'object') {
              for (const [k, fd] of Object.entries(fieldValues)) {
                const v = (fd as any)?.value;
                if (v !== null && v !== undefined && v !== '') {
                  factsForWorkflowAI[k] = { ...(factsForWorkflowAI[k] && typeof factsForWorkflowAI[k] === 'object' ? factsForWorkflowAI[k] : {}), value: v };
                }
              }
            }
          }
        }
      }
      // Don't pass stale workflow-derived values to the AI (e.g. collateralClass: OTHER); remove those keys so the rule is evaluated only from other facts
      workflowFieldKeys.forEach(k => {
        delete factsForWorkflowAI[k];
      });
      console.log('[HomePage] Facts for workflow AI: keys', Object.keys(factsForWorkflowAI), '| collateralCode present:', 'collateralCode' in factsForWorkflowAI, factsForWorkflowAI.collateralCode != null ? `value=${factsForWorkflowAI.collateralCode?.value ?? factsForWorkflowAI.collateralCode}` : '');

      // AI evaluation for all fields with workflow rules
      const evidenceDefs = item.evidenceDefinitions ?? [];
      for (const evidenceDef of evidenceDefs) {
        const fieldDefs = evidenceDef.fieldDefinitions ?? [];
        const evidenceName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence';
        for (const fieldDef of fieldDefs) {
          if (!fieldDef.workflowRules?.trim()) continue;
          const key = fieldDef.key;
          const alreadyHas = workflowFactsToSave[key]?.value !== null && workflowFactsToSave[key]?.value !== undefined && workflowFactsToSave[key]?.value !== '';
          if (alreadyHas) continue;
          try {
            const aiValue = await evaluateFieldWorkflowRuleWithAI(
              fieldDef.workflowRules,
              factsForWorkflowAI,
              { key: fieldDef.key, type: fieldDef.type, enumValues: fieldDef.enumValues }
            );
            console.log('[HomePage] Workflow AI result:', { field: key, evidence: evidenceName, aiValue });
            if (aiValue !== undefined && aiValue !== null && aiValue !== '') {
              workflowFactsToSave[key] = {
                value: aiValue,
                source: 'EXTRACTED',
                extractedFrom: 'Workflow rule (AI)',
                confidence: 'high',
              };
              if (!extractedFields[evidenceName]) extractedFields[evidenceName] = {};
              (extractedFields[evidenceName] as Record<string, { value: any; source?: string }>)[key] = {
                value: aiValue,
                source: 'Workflow rule',
              };
              // Add to factsForWorkflowAI so later workflow fields can depend on this (e.g. dwellingSecured depends on collateralClass)
              factsForWorkflowAI[key] = { value: aiValue };
            }
          } catch (e) {
            console.warn('[HomePage] AI workflow evaluation failed for', key, e);
          }
        }
      }

      if (Object.keys(workflowFactsToSave).length > 0 && selectedAssetForDetails.id && currentOrganization?.id) {
        const setResult = await assetFactsService.setFacts(selectedAssetForDetails.id, workflowFactsToSave, currentOrganization.id);
        if (setResult.success) {
          console.log('[HomePage] Saved workflow-derived facts to asset:', Object.keys(workflowFactsToSave));
        } else {
          console.warn('[HomePage] Failed to save workflow-derived facts:', setResult.error);
        }
      }

      // Merge workflow-derived values into facts for the rest of this run (and update state so UI sees them).
      // Replace the fact entirely for workflow keys so we do NOT keep old extractedValues (file-derived OTHER);
      // otherwise the Fields panel would still show the first extractedValue (file) instead of the workflow value.
      let assetFactsForRun = assetFacts;
      if (Object.keys(workflowFactsToSave).length > 0) {
        assetFactsForRun = { ...assetFacts };
        for (const [k, v] of Object.entries(workflowFactsToSave)) {
          assetFactsForRun[k] = {
            value: v.value,
            source: 'EXTRACTED',
            extractedFrom: 'Workflow rule',
            confidence: 'high' as const,
            extractedValues: [{ value: v.value, fileName: 'Workflow rule', confidence: 'high' as const }],
          };
        }
        setAssetFacts(assetFactsForRun);
      }

      // 1.5. Re-validate and update evidence records for this item (including workflow-only evidences)
      if (item.evidenceDefinitions && item.evidenceDefinitions.length > 0 && selectedCompletionForDetails.id) {
        const { validateFieldValueFromEvidenceField } = await import('@/services/checklistValidationService');

        // Build merged facts: asset facts + field values from all evidences on this item
        // so workflow-only evidences can use fields (e.g. collateralCode) from other evidences
        const existingEvidenceResult = await checklistEvidenceService.getEvidenceByItemResult(
          selectedCompletionForDetails.id,
          item.text
        );
        const allFactsForItem: Record<string, any> = { ...assetFactsForRun };
        for (const ev of existingEvidenceResult.success && existingEvidenceResult.data ? existingEvidenceResult.data : []) {
          const fieldValues = (ev as any).fieldValues;
          if (fieldValues && typeof fieldValues === 'object') {
            for (const [key, fieldData] of Object.entries(fieldValues)) {
              const v = (fieldData as any)?.value;
              if (v !== null && v !== undefined && v !== '') {
                const existing = allFactsForItem[key];
                const existingVal = existing?.value ?? existing?.extractedValues?.[0]?.value;
                if (existingVal === null || existingVal === undefined || existingVal === '') {
                  allFactsForItem[key] = { ...(existing && typeof existing === 'object' ? existing : {}), value: v };
                }
              }
            }
          }
        }

        for (const evidenceDef of item.evidenceDefinitions) {
          const hasFields = evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0;

          if (!hasFields) {
            // Workflow-only evidence — evaluate using workflow rules + all available facts (asset facts + other evidences' fields)
            const satisfied = evidenceDef.workflowRules
              ? evaluateWorkflowOnlyEvidence(evidenceDef.workflowRules, allFactsForItem)
              : false;
            const validationStatus = satisfied ? 'COMPLETE' : 'INCOMPLETE';
            const validationSummary = satisfied
              ? 'Workflow condition met'
              : (evidenceDef.workflowRules ? 'Workflow condition not met' : 'No workflow rules defined');

            const targetEvidenceName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence';
            const existingEvidence = existingEvidenceResult.success && existingEvidenceResult.data
              ? existingEvidenceResult.data.find((ev: any) =>
                  (ev.evidenceName || ev.categoryCode) === targetEvidenceName
                )
              : null;

            const fieldStatus = {
              totalFields: 0,
              requiredFields: 0,
              populatedFields: 0,
              validatedFields: 0,
              missingFields: [] as string[],
              invalidFields: [] as string[],
            };

            if (existingEvidence?.id) {
              await checklistEvidenceService.updateEvidence(existingEvidence.id, {
                evidenceName: targetEvidenceName,
                fieldStatus,
                validationStatus,
                validationSummary,
                fieldValues: {},
                updatedAt: serverTimestamp(),
              });
            } else {
              await checklistEvidenceService.addEvidence(
                {
                  checklistCompletionId: selectedCompletionForDetails.id,
                  itemText: item.text,
                  evidenceType: 'DATA_ONLY',
                  evidenceName: targetEvidenceName,
                  source: 'CORE_SYSTEM',
                  organizationId: currentOrganization.id,
                  addedByUserId: user.id,
                  fieldStatus,
                  validationStatus,
                  validationSummary,
                  fieldValues: {},
                },
                currentOrganization
              );
            }
            continue;
          }

          // Extract field values and validate (evidences with field definitions)
          const fieldDefs = evidenceDef.fieldDefinitions ?? [];
          const evidenceName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence';
          const extractedForEvidence = extractedFields[evidenceName] || {};
          const fieldData: Record<string, { value: any; populated: boolean; validated: boolean; source?: string }> = {};
          const missingFields: string[] = [];
          const invalidFields: string[] = [];
          
          for (const fieldDef of fieldDefs) {
            const fact = assetFactsForRun[fieldDef.key];
            let value: any = null;
            let source: string | undefined = undefined;
            
            // Use value from extractedFields (AI workflow results) or assetFactsForRun (merged workflow-derived facts)
            const extractedField = extractedForEvidence[fieldDef.key];
            if (extractedField?.value !== null && extractedField?.value !== undefined && extractedField?.value !== '') {
              value = extractedField.value;
              source = extractedField.source || 'Workflow rule';
            }
            // Fallback: for workflow fields, use merged asset fact so we get AI result even if extractedFields key mismatched
            if ((value == null || value === '') && fieldDef.workflowRules?.trim()) {
              const workflowFact = assetFactsForRun[fieldDef.key];
              const workflowVal = workflowFact?.value ?? (workflowFact as any)?.extractedValues?.[0]?.value;
              if (workflowVal !== null && workflowVal !== undefined && workflowVal !== '') {
                value = workflowVal;
                source = source || 'Workflow rule';
              }
            }
            if (value == null && fact) {
              if (fact.source === 'REVIEWER_OVERRIDE' && fact.value !== null && fact.value !== undefined && fact.value !== '') {
                value = fact.value;
                source = fact.extractedFrom;
              } else if (fact.extractedValues && fact.extractedValues.length > 0) {
                const sorted = fact.extractedValues.sort((a: any, b: any) => {
                  const confMap: Record<string, number> = { high: 3, medium: 2, low: 1 };
                  return (confMap[b.confidence || 'low'] || 0) - (confMap[a.confidence || 'low'] || 0);
                });
                value = sorted[0].value;
                source = sorted[0].fileName;
              } else if (fact.value !== null && fact.value !== undefined && fact.value !== '') {
                value = fact.value;
                source = fact.extractedFrom;
              }
            }
            
            // Workflow-derived values come from AI (evaluateFieldWorkflowRuleWithAI) in the earlier pass; value is set from extractedField above.

            const populated = value !== null && value !== undefined && value !== '';
            if (fieldDef.key === 'collateralClass') {
              console.log('[HomePage] fieldData.collateralClass source:', { fromExtracted: extractedField?.value, fromWorkflowFact: (assetFactsForRun[fieldDef.key] as any)?.value, finalValue: value, evidenceName });
            }
            fieldData[fieldDef.key] = {
              value,
              populated,
              validated: false,
              source
            };
            
            if (fieldDef.required && !populated) {
              missingFields.push(fieldDef.key);
            } else if (populated) {
              const validationResult = validateFieldValueFromEvidenceField(
                fieldDef,
                value,
                assetFactsForRun,
                selectedCompletionForDetails.itemResults?.[item.text] as any
              );
              
              if (validationResult.valid) {
                fieldData[fieldDef.key].validated = true;
              } else {
                invalidFields.push(fieldDef.key);
                (fieldData[fieldDef.key] as any).validationError = validationResult.reason;
              }
            } else {
              fieldData[fieldDef.key].validated = true; // Optional field with no value
            }
          }
          
          const totalFields = fieldDefs.length;
          const requiredFields = fieldDefs.filter(f => f.required).length;
          const populatedFields = Object.values(fieldData).filter(f => f.populated).length;
          const validatedFields = Object.values(fieldData).filter(f => f.validated).length;
          
          const requiredFieldsValidated = fieldDefs
            .filter(f => f.required)
            .every(f => fieldData[f.key].populated && fieldData[f.key].validated);
          
          let validationStatus: 'COMPLETE' | 'INCOMPLETE' | 'INVALID';
          if (missingFields.length > 0) {
            validationStatus = 'INCOMPLETE';
          } else if (invalidFields.length > 0 || !requiredFieldsValidated) {
            validationStatus = 'INVALID';
          } else {
            validationStatus = 'COMPLETE';
          }
          
          let validationSummary = '';
          if (validationStatus === 'COMPLETE') {
            validationSummary = `All ${requiredFields} required fields populated and validated`;
          } else if (missingFields.length > 0) {
            const missingLabels = missingFields.map(key => {
              const fieldDef = evidenceDef.fieldDefinitions?.find(f => f.key === key);
              return fieldDef?.label || key;
            });
            validationSummary = `Missing required fields: ${missingLabels.join(', ')}`;
          } else if (invalidFields.length > 0) {
            const invalidDetails = invalidFields.map(key => {
              const fieldDef = evidenceDef.fieldDefinitions?.find(f => f.key === key);
              const fieldInfo = fieldData[key];
              const error = (fieldInfo as any).validationError || 'Invalid value';
              return `${fieldDef?.label || key}: ${error}`;
            });
            validationSummary = `Validation failed: ${invalidDetails.join('; ')}`;
          }
          
          // Update existing evidence or create new (reuse existingEvidenceResult from top of loop)
          const targetEvidenceName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence';
          
          // Match evidence by evidenceName or categoryCode (for legacy records)
          const allMatchingEvidence = existingEvidenceResult.success && existingEvidenceResult.data
            ? existingEvidenceResult.data.filter(ev => {
                const evName = ev.evidenceName || (ev as any).categoryCode || '';
                return evName === targetEvidenceName || 
                       evName === evidenceDef.evidenceName ||
                       evName === (evidenceDef as any).categoryCode;
              })
            : [];
          
          // If there are multiple matches, we need to deduplicate - keep the most recent one
          let existingEvidence = allMatchingEvidence.length > 0 ? allMatchingEvidence[0] : null;
          
          // If there are duplicates, delete the older ones and keep only the most recent
          if (allMatchingEvidence.length > 1) {
            console.log(`[HomePage] Found ${allMatchingEvidence.length} duplicate evidence records for "${targetEvidenceName}". Deduplicating...`);
            // Sort by addedAt (most recent first)
            const sorted = allMatchingEvidence.sort((a, b) => {
              const aTime = a.addedAt?.toMillis?.() || a.addedAt || 0;
              const bTime = b.addedAt?.toMillis?.() || b.addedAt || 0;
              return bTime - aTime;
            });
            existingEvidence = sorted[0]; // Keep the most recent
            
            // Delete the older duplicates
            for (let i = 1; i < sorted.length; i++) {
              if (sorted[i].id) {
                console.log(`[HomePage] Deleting duplicate evidence record: ${sorted[i].id}`);
                await checklistEvidenceService.deleteEvidence(sorted[i].id!);
              }
            }
            // Reload evidence after deduplication to ensure UI is in sync
            const reloadedEvidenceResult = await checklistEvidenceService.getEvidenceByItemResult(
              selectedCompletionForDetails.id,
              item.text
            );
            if (reloadedEvidenceResult.success && reloadedEvidenceResult.data) {
              setItemEvidenceMap(prev => ({
                ...prev,
                [item.text]: reloadedEvidenceResult.data || []
              }));
            }
          }
          
          if (existingEvidence?.id) {
            const fieldDataKeys = Object.keys(fieldData);
            const collateralNormalized = fieldData.collateralAddressNormalized;
            console.log('[HomePage] Saving evidence:', existingEvidence.id, 'evidenceName:', evidenceDef.evidenceName, '| fieldData keys:', fieldDataKeys.length, fieldDataKeys);
            console.log('[HomePage] fieldData.collateralAddressNormalized:', collateralNormalized?.value !== undefined ? JSON.stringify(String(collateralNormalized.value).slice(0, 50)) : '(undefined or empty)');
            // Update existing evidence - always ensure evidenceName is set
            const updateData: any = {
              // Always set evidenceName (required field) - use from definition or existing value
              evidenceName: evidenceDef.evidenceName || existingEvidence.evidenceName || (existingEvidence as any).categoryCode || 'Unknown Evidence',
              fieldStatus: {
                totalFields,
                requiredFields,
                populatedFields,
                validatedFields,
                missingFields,
                invalidFields,
              },
              validationStatus,
              validationSummary,
              fieldValues: fieldData,
              updatedAt: serverTimestamp(),
            };
            
            console.log('[HomePage] Updating evidence:', existingEvidence.id, 'evidenceName:', updateData.evidenceName, 'with validationStatus:', validationStatus);
            const updateResult = await checklistEvidenceService.updateEvidence(existingEvidence.id, updateData);
            if (!updateResult.success) {
              console.error('[HomePage] Failed to update evidence:', updateResult.error);
            } else {
              console.log('[HomePage] Successfully updated evidence:', existingEvidence.id);
            }
          } else {
            // Ensure we have an evidenceName - use from definition or fallback
            const evidenceName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence';
            console.log('[HomePage] Creating new evidence for:', evidenceName);
            const addResult = await checklistEvidenceService.addEvidence(
              {
                checklistCompletionId: selectedCompletionForDetails.id,
                itemText: item.text,
                evidenceType: 'DATA_ONLY',
                evidenceName: evidenceName,
                source: 'CORE_SYSTEM',
                organizationId: currentOrganization.id,
                addedByUserId: user.id,
                fieldStatus: {
                  totalFields,
                  requiredFields,
                  populatedFields,
                  validatedFields,
                  missingFields,
                  invalidFields,
                },
                validationStatus,
                validationSummary,
                fieldValues: fieldData,
              },
              currentOrganization
            );
            if (!addResult.success) {
              console.error('[HomePage] Failed to create evidence:', addResult.error);
            } else {
              console.log('[HomePage] Successfully created evidence:', addResult.id);
            }
          }
        }
      }

      // 2. Call AI evaluation
      const aiResult = await evaluateChecklistItemWithAI(item, extractedFields, assetFactsForRun);
      console.log('[HomePage] AI evaluation result:', aiResult);

      if (!aiResult) {
        throw new Error('AI evaluation returned no result');
      }

      // 3. Update completion item result
      const currentItemResult = selectedCompletionForDetails.itemResults?.[item.text] || {};
      const updatedItemResult = {
        ...currentItemResult,
        status: aiResult.status,
        findingText: aiResult.findingText || (aiResult.reasoning ? aiResult.reasoning : ''),
        updatedBy: user.id,
        updatedAt: serverTimestamp(),
      };

      // 4. Update completion in state
      const updatedCompletion = {
        ...selectedCompletionForDetails,
        itemResults: {
          ...(selectedCompletionForDetails.itemResults || {}),
          [item.text]: updatedItemResult,
        },
      };

      // 5. Recalculate overall completion stats
      const itemResults = updatedCompletion.itemResults || {};
      const items = checklistDetails.items || [];
      let itemsCompleted = 0;
      let itemsIncomplete = 0;
      let itemsNeedsReview: string[] = [];
      let itemsFailed: string[] = [];

      items.forEach((checklistItem: ChecklistItem) => {
        const result = itemResults[checklistItem.text];
        const status = result?.status;
        if (status === 'PASS') {
          itemsCompleted++;
        } else if (status === 'NEEDS_REVIEW') {
          itemsNeedsReview.push(checklistItem.text);
          itemsIncomplete++;
        } else if (status === 'FAIL') {
          itemsFailed.push(checklistItem.text);
          itemsIncomplete++;
        } else {
          itemsIncomplete++;
        }
      });

      // Update completionSummary
      updatedCompletion.completionSummary = {
        totalItems: items.length,
        completedItems: itemsCompleted,
        incompleteItems: itemsIncomplete,
        missingRequiredFields: updatedCompletion.completionSummary?.missingRequiredFields || [],
        missingRequiredEvidence: updatedCompletion.completionSummary?.missingRequiredEvidence || [],
        needsReviewItems: itemsNeedsReview,
      };

      // Determine overall status (using correct enum values)
      if (itemsFailed.length > 0) {
        updatedCompletion.status = 'pending_review';
      } else if (itemsNeedsReview.length > 0) {
        updatedCompletion.status = 'pending_review';
      } else if (itemsIncomplete > 0) {
        updatedCompletion.status = 'in_progress';
      } else if (itemsCompleted === items.length) {
        updatedCompletion.status = 'complete';
      } else {
        updatedCompletion.status = 'in_progress';
      }

      // 6. Save to Firestore
      if (selectedCompletionForDetails.id) {
        const updateResult = await checklistCompletionService.updateCompletion(
          selectedCompletionForDetails.id,
          {
            itemResults: updatedCompletion.itemResults,
            completionSummary: updatedCompletion.completionSummary,
            status: updatedCompletion.status,
            updatedAt: serverTimestamp(),
          }
        );

        if (!updateResult.success) {
          throw new Error(updateResult.error || 'Failed to update completion');
        }
      }

      // 7. Reload evidence list to reflect updated evidence with validation status
      if (selectedCompletionForDetails.id && item.evidenceDefinitions && item.evidenceDefinitions.length > 0) {
        try {
          const evidenceResult = await checklistEvidenceService.getEvidenceByItemResult(
            selectedCompletionForDetails.id,
            item.text
          );
          if (evidenceResult.success && evidenceResult.data) {
            console.log('[HomePage] Reloaded evidence after re-evaluation:', evidenceResult.data);
            // Update evidence map for this item to show updated validation status
            setItemEvidenceMap(prev => ({
              ...prev,
              [item.text]: evidenceResult.data || []
            }));
          }
        } catch (error) {
          console.error('[HomePage] Error reloading evidence after re-evaluation:', error);
        }
      }

      // 7.5. Do NOT refetch asset facts when we just saved workflow-derived values - we already set assetFacts via setAssetFacts(assetFactsForRun) with the correct values. Refetching can return stale/cached data (e.g. old file-derived OTHER) and overwrite the UI.

      // 8. Update local state
      setSelectedCompletionForDetails(updatedCompletion);

      showNotification(`Item "${item.text}" re-evaluated successfully`, 'success');
    } catch (error: any) {
      console.error('[HomePage] Error re-evaluating item:', error);
      showNotification(
        `Failed to re-evaluate item: ${error.message || 'Unknown error'}`,
        'error'
      );
    } finally {
      // Remove loading state
      setReevaluatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.text);
        return newSet;
      });
    }
  };

  // Function to mark a checklist item as passed (manual review)
  const handleMarkAsPassed = async (item: ChecklistItem) => {
    if (!selectedAssetForDetails || !selectedCompletionForDetails || !user?.id) {
      showNotification('Missing required data for marking item as passed', 'error');
      return;
    }

    if (!selectedCompletionForDetails.id) {
      showNotification('Completion ID is required', 'error');
      return;
    }

    // Set loading state
    setMarkingAsPassedItems(prev => new Set(prev).add(item.text));

    try {
      console.log('[HomePage] Marking item as passed:', item.text);

      // Update item result to PASS status
      const updateResult = await checklistCompletionService.updateItemResult(
        selectedCompletionForDetails.id,
        item.text,
        {
          status: 'PASS',
          findingText: `Manually reviewed and approved by ${user.name || user.email} on ${new Date().toLocaleDateString()}`,
        },
        user.id
      );

      if (!updateResult.success) {
        throw new Error(updateResult.error || 'Failed to update item status');
      }

      // Reload completion to get latest data
      const reloadResult = await checklistCompletionService.getCompletion(selectedCompletionForDetails.id);
      if (reloadResult.success && reloadResult.data) {
        const updatedCompletion = reloadResult.data;
        const itemResults = updatedCompletion.itemResults || {};
        const items = checklistDetails?.items || [];
        
        let itemsCompleted = 0;
        let itemsIncomplete = 0;
        let itemsNeedsReview: string[] = [];
        let itemsFailed: string[] = [];

        items.forEach((checklistItem: ChecklistItem) => {
          const result = itemResults[checklistItem.text];
          const status = result?.status;
          if (status === 'PASS') {
            itemsCompleted++;
          } else if (status === 'NEEDS_REVIEW') {
            itemsNeedsReview.push(checklistItem.text);
            itemsIncomplete++;
          } else if (status === 'FAIL') {
            itemsFailed.push(checklistItem.text);
            itemsIncomplete++;
          } else {
            itemsIncomplete++;
          }
        });

        // Update completion summary and status
        const completionSummary = {
          totalItems: items.length,
          completedItems: itemsCompleted,
          incompleteItems: itemsIncomplete,
          missingRequiredFields: updatedCompletion.completionSummary?.missingRequiredFields || [],
          missingRequiredEvidence: updatedCompletion.completionSummary?.missingRequiredEvidence || [],
          needsReviewItems: itemsNeedsReview,
        };

        // Determine overall status
        let overallStatus: 'complete' | 'pending_review' | 'in_progress' | 'paused';
        if (itemsFailed.length > 0) {
          overallStatus = 'pending_review';
        } else if (itemsNeedsReview.length > 0) {
          overallStatus = 'pending_review';
        } else if (itemsIncomplete > 0) {
          overallStatus = 'in_progress';
        } else if (itemsCompleted === items.length) {
          overallStatus = 'complete';
        } else {
          overallStatus = 'in_progress';
        }

        // Update completion with new summary and status
        await checklistCompletionService.updateCompletion(
          selectedCompletionForDetails.id,
          {
            completionSummary,
            status: overallStatus,
            updatedAt: serverTimestamp(),
          }
        );

        // Reload again to get the updated completion
        const finalReloadResult = await checklistCompletionService.getCompletion(selectedCompletionForDetails.id);
        if (finalReloadResult.success && finalReloadResult.data) {
          setSelectedCompletionForDetails(finalReloadResult.data);
        }
      }

      showNotification('Item marked as passed successfully', 'success');
    } catch (error: any) {
      console.error('[HomePage] Error marking item as passed:', error);
      showNotification(`Failed to mark item as passed: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setMarkingAsPassedItems(prev => {
        const next = new Set(prev);
        next.delete(item.text);
        return next;
      });
    }
  };

  // Function to toggle asset row expansion
  const toggleAssetExpansion = (assetId: string) => {
    setExpandedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  // Function to export compliance review report
  const handleExportComplianceReport = async (asset: Asset, completion: ChecklistCompletion) => {
    try {
      // Load checklist details if not already loaded
      let checklist: Checklist | null = null;
      if (completion.checklistId) {
        const checklistResult = await checklistService.getChecklist(completion.checklistId);
        if (checklistResult.success && checklistResult.data) {
          checklist = checklistResult.data;
        }
      }

      if (!checklist) {
        showNotification('Checklist details not found', 'error');
        return;
      }

      // Get asset metadata
      const assetType = assetTypes.find(at => at.id === asset.assetTypeId);
      const loanOfficerField = assetTypes
        .flatMap(at => at.customFields || [])
        .find(cf => cf.type === 'loan_officer_picker');
      const loanNumberField = assetTypes
        .flatMap(at => at.customFields || [])
        .find(cf => cf.type === 'alphanumeric');
      
      const loanNumber = loanNumberField ? asset.fieldValues?.[loanNumberField.name] : '';
      const loanOfficerId = loanOfficerField ? asset.fieldValues?.[loanOfficerField.name] : '';
      const loanOfficer = loanOfficerId ? eligibleLoanOfficers.find(lo => lo.id === loanOfficerId)?.name : '';
      const borrower = asset.assetName || 'Unknown';
      const loanId = loanNumber || asset.id || '';

      // Load evidence for this completion
      const evidenceResult = await checklistEvidenceService.getEvidenceByCompletion(completion.id || '');
      const evidenceList: ChecklistEvidence[] = evidenceResult.success && evidenceResult.data ? evidenceResult.data : [];
      
      // Create evidence map by item text
      const evidenceByItem: Record<string, ChecklistEvidence[]> = {};
      evidenceList.forEach(evidence => {
        if (evidence.itemText) {
          if (!evidenceByItem[evidence.itemText]) {
            evidenceByItem[evidence.itemText] = [];
          }
          evidenceByItem[evidence.itemText].push(evidence);
        }
      });

      // Load asset facts
      const factsResult = await assetFactsService.getAssetFacts(asset.id || '');
      const assetFactsData: Record<string, any> = factsResult.success && factsResult.data?.facts ? factsResult.data.facts : {};

      // Helper function to format field values
      const formatFieldValue = (val: any): any => {
        if (val === null || val === undefined) return '';
        if (val instanceof Date) return val;
        if (typeof val === 'object' && val.toDate && typeof val.toDate === 'function') {
          return val.toDate();
        }
        if (typeof val === 'string') {
          // Try to parse as date
          const date = new Date(val);
          if (!isNaN(date.getTime()) && val.match(/^\d{4}-\d{2}-\d{2}/)) {
            return date;
          }
        }
        return val;
      };

      // Helper function to format date for display
      const formatDateDisplay = (val: any): string => {
        if (!val) return '';
        const date = formatFieldValue(val);
        if (date instanceof Date) {
          return date.toISOString().split('T')[0];
        }
        return String(val);
      };

      // Create a new workbook
      const workbook = XLSX.utils.book_new();

      // ===== SHEET 1: METADATA =====
      const metadataRows = [
        ['Field', 'Value'],
        ['Loan ID', loanId],
        ['Borrower', borrower],
        ['Loan Number', loanNumber || ''],
        ['Loan Officer', loanOfficer || ''],
        ['Asset Name', asset.assetName || ''],
        ['Asset Type', assetType?.name || ''],
        ['Checklist Name', checklist.name || ''],
        ['Completion Date', completion.completedAt 
          ? formatDateDisplay(completion.completedAt)
          : ''],
        ['Completed By', completion.completedByName || (completion.completedBy === 'AUTO' ? 'System' : 'Unknown')],
        ['Status', completion.status || ''],
      ];

      const metadataSheet = XLSX.utils.aoa_to_sheet(metadataRows);
      XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Metadata');

      // ===== SHEETS 2+: ONE PER CHECKLIST ITEM =====
      const items = checklist.items || [];
      for (const item of items) {
        const itemResult = completion.itemResults?.[item.text];
        const status = itemResult?.status || 'PENDING';
        const findingText = itemResult?.findingText || '';
        const reviewer = completion.completedByName || (completion.completedBy === 'AUTO' ? 'System' : 'Unknown');
        const reviewDate = completion.completedAt 
          ? formatDateDisplay(completion.completedAt)
          : '';

        // Get evidence for this item
        const itemEvidence = evidenceByItem[item.text] || [];

        // Build sheet data for this item
        const itemSheetRows: any[][] = [];

        // Item header section
        itemSheetRows.push(['Checklist Item Details']);
        itemSheetRows.push(['Item Text', item.text]);
        itemSheetRows.push(['Status', status]);
        itemSheetRows.push(['Applicable', status === 'NA' ? 'N' : 'Y']);
        itemSheetRows.push(['Finding Text', findingText]);
        itemSheetRows.push(['Reviewer / System', reviewer]);
        itemSheetRows.push(['Review Date', reviewDate]);
        itemSheetRows.push([]); // Empty row

        // Evidence section
        itemSheetRows.push(['Evidence']);
        if (itemEvidence.length > 0) {
          itemSheetRows.push(['Evidence Name', 'Type', 'Source', 'Status', 'Validation Status']);
          itemEvidence.forEach(evidence => {
            itemSheetRows.push([
              evidence.evidenceName || 'Unknown',
              evidence.evidenceType || '',
              evidence.source || '',
              evidence.validationStatus || '',
              evidence.fieldStatus ? 
                `${evidence.fieldStatus.populatedFields}/${evidence.fieldStatus.requiredFields} fields populated` : 
                ''
            ]);
          });
        } else {
          itemSheetRows.push(['No evidence found']);
        }
        itemSheetRows.push([]); // Empty row

        // Fields section - organized by evidence
        if (item.evidenceDefinitions && item.evidenceDefinitions.length > 0) {
          itemSheetRows.push(['Evidence Fields']);
          itemSheetRows.push(['Evidence', 'Field Label', 'Field Key', 'Value', 'Source', 'Confidence']);

          item.evidenceDefinitions.forEach(evidenceDef => {
            const evidenceName = evidenceDef.evidenceName || 'Unknown';
            const evidenceForThisDef = itemEvidence.filter(e => 
              e.evidenceName === evidenceName || 
              (e.evidenceName === undefined && evidenceName === 'Unknown')
            );

            if (evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0) {
              evidenceDef.fieldDefinitions.forEach(fieldDef => {
                const fieldKey = fieldDef.key || '';
                const fieldLabel = fieldDef.label || fieldKey;
                
                // Get value from asset facts
                const fact = assetFactsData[fieldKey] as any;
                const value = formatFieldValue(fact?.value || fact?.extractedValues?.[0]?.value || '');
                const source = fact?.source || fact?.extractedValues?.[0]?.fileName || '';
                const confidence = fact?.confidence || fact?.extractedValues?.[0]?.confidence || '';

                itemSheetRows.push([
                  evidenceName,
                  fieldLabel,
                  fieldKey,
                  value,
                  source,
                  confidence
                ]);
              });
            } else {
              // No fields defined for this evidence
              itemSheetRows.push([evidenceName, 'No fields defined', '', '', '', '']);
            }
          });
        } else {
          itemSheetRows.push(['No evidence definitions found for this item']);
        }

        // Create sheet with sanitized name (Excel sheet names have restrictions)
        const sheetName = item.text
          .substring(0, 31) // Excel sheet name max length
          .replace(/[\\\/\?\*\[\]:]/g, '_') // Replace invalid characters
          .trim() || `Item_${items.indexOf(item) + 1}`;

        const itemSheet = XLSX.utils.aoa_to_sheet(itemSheetRows);
        
        // Set column widths for better readability
        const colWidths = [
          { wch: 20 }, // Evidence/Field
          { wch: 25 }, // Field Label
          { wch: 20 }, // Field Key
          { wch: 30 }, // Value
          { wch: 20 }, // Source
          { wch: 15 }  // Confidence
        ];
        itemSheet['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(workbook, itemSheet, sheetName);
      }

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Compliance_Review_${loanNumber || asset.id || 'Report'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showNotification('Compliance review report exported successfully', 'success');
    } catch (error: any) {
      console.error('[HomePage] Error exporting compliance report:', error);
      showNotification(`Failed to export report: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const applyFilters = () => {
    let filtered = [...tasks];

    // Apply "My Tasks" filter if enabled
    if (filterMyTasks) {
      filtered = filtered.filter(task => userTaskIds.has(task.id));
    }

    // Filter to show tasks that are due soon (within 30 days), overdue, or pending review
    filtered = filtered.filter(task => {
      // Always show overdue tasks
      if (task.status === 'overdue') {
        return true;
      }

      // Always show pending review tasks
      if (task.status === 'pending_review') {
        return true;
      }

      // Show pending tasks due within 30 days
      if (task.status === 'pending') {
        const daysUntilDue = Math.ceil((task.dueDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
        return daysUntilDue >= 0 && daysUntilDue <= 30;
      }

      return false;
    });

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(task =>
        task.templateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.locationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by location - use global filter if active, otherwise use local filter
    if (hasActiveFilters && selectedLocationIds.length > 0) {
      // Use global location filter - filter by locationId
      filtered = filtered.filter(task => task.locationId && selectedLocationIds.includes(task.locationId));
    } else if (taskFilterLocation !== 'all') {
      // Use local location filter - filter by locationName
      filtered = filtered.filter(task => task.locationName === taskFilterLocation);
    }

    // Filter by risk level
    if (taskFilterRiskLevel !== 'all') {
      filtered = filtered.filter(task => {
        const riskLevel = task.riskLevel || '';
        return riskLevel.toLowerCase() === taskFilterRiskLevel.toLowerCase();
      });
    }

    // Sort tasks
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (taskSortBy) {
        case 'task':
          comparison = a.templateName.localeCompare(b.templateName);
          break;
        case 'risk':
          const riskOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
          const aRisk = riskOrder[a.riskLevel as keyof typeof riskOrder] || 0;
          const bRisk = riskOrder[b.riskLevel as keyof typeof riskOrder] || 0;
          comparison = aRisk - bRisk;
          break;
        case 'location':
          comparison = a.locationName.localeCompare(b.locationName);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        case 'due':
          comparison = a.dueDate.getTime() - b.dueDate.getTime();
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        default:
          comparison = 0;
      }
      
      return taskSortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredTasks(filtered);
  };

  // Calculate metrics
  const dueSoonCount = tasks.filter(task => {
    const daysUntilDue = Math.ceil((task.dueDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilDue >= 0 && daysUntilDue <= 30 && task.status === 'pending';
  }).length;

  const overdueCount = tasks.filter(task => task.status === 'overdue').length;

  const onTimeRate = tasks.length > 0 
    ? Math.round((tasks.filter(task => task.status === 'completed').length / tasks.length) * 100)
    : 100;

  const notificationCount = tasks.filter(task => 
    task.status === 'pending' || task.status === 'overdue'
  ).length;

  const pendingReviewCount = tasks.filter(task => task.status === 'pending_review').length;
  const isHighRiskDueSoonOrOverdue = (task: TaskWithDetails) => {
    const riskLevel = (task.riskLevel || '').toLowerCase();
    if (riskLevel !== 'high') return false;

    if (task.status === 'overdue') {
      return true;
    }

    if (task.status !== 'pending') {
      return false;
    }

    const daysUntilDue = Math.ceil((task.dueDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilDue >= 0 && daysUntilDue <= 30;
  };

  const highRiskDueSoonCount = tasks.filter(isHighRiskDueSoonOrOverdue).length;

  // Improved loading skeleton component with shimmer effect
  const MetricCardSkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative overflow-hidden">
      {/* Shimmer effect overlay */}
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
      
      <div className="flex items-center justify-between mb-2 relative z-10">
        <div className="w-6 h-6 bg-gray-200 rounded" />
        <div className="h-4 w-20 bg-gray-200 rounded" />
      </div>
      <div className="h-9 w-16 bg-gray-200 rounded mb-2 relative z-10" />
      <div className="h-4 w-24 bg-gray-200 rounded relative z-10" />
    </div>
  );

  // Scorecard skeleton component
  const ScorecardSkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative overflow-hidden">
      {/* Shimmer effect overlay */}
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
      
      <div className="flex items-center gap-2 mb-4 relative z-10">
        <div className="w-5 h-5 bg-gray-200 rounded" />
        <div className="h-6 w-40 bg-gray-200 rounded" />
      </div>
      <div className="space-y-3 relative z-10">
        <div className="h-16 bg-gray-100 rounded-lg" />
        <div className="h-16 bg-gray-100 rounded-lg" />
        <div className="h-16 bg-gray-100 rounded-lg" />
      </div>
    </div>
  );

  // Site Metrics skeleton component
  const SiteMetricsSkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative overflow-hidden">
      {/* Shimmer effect overlay */}
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
      
      <div className="flex items-center justify-between mb-4 relative z-10">
        <div className="h-6 w-32 bg-gray-200 rounded" />
        <div className="w-8 h-8 bg-gray-200 rounded-full" />
      </div>
      <div className="relative z-10">
        <div className="grid grid-cols-5 gap-4 mb-3 pb-2 border-b border-gray-200">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-4 bg-gray-200 rounded" />
          ))}
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="grid grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5].map((j) => (
                <div key={j} className="h-8 bg-gray-100 rounded" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Check if subscription is cancelled
  const isSubscriptionCancelled = subscription?.status === 'canceled';
  
  // Check if user is on free (communicate) plan
  // If subscription is null or plan is null, treat as free plan
  const normalizedPlan = subscription?.plan ? normalizeSubscriptionPlan(subscription.plan) : null;
  const isFreePlan = !subscription || !subscription.plan || normalizedPlan === 'communicate';
  const isOperatePlan = normalizedPlan === 'operate';

  return (
    <div className="space-y-6">
      {/* Cancelled Subscription Banner */}
      {isSubscriptionCancelled && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Subscription Cancelled</h3>
              <p className="text-sm text-red-800 mb-4">
                Your subscription has been cancelled. You can view your tasks but cannot complete or review them until you renew your subscription.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  Renew Subscription
                </button>
                        <button
                          onClick={() => navigate('/subscription')}
                          className="px-4 py-2 bg-white text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                        >
                          View Plans
                        </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Permission Banner */}
      {showNotificationBanner && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Bell className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">Enable Notifications</h3>
              <p className="text-sm text-yellow-800 mb-4">
                Get notified about task due dates, overdue tasks, and important updates. Click the button below to enable browser notifications.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    try {
                      const { requestPermissionAndInitializeFCM } = await import('@/services/fcmService');
                      const success = await requestPermissionAndInitializeFCM();
                      if (success) {
                        setShowNotificationBanner(false);
                        showNotification('Notifications enabled successfully!', 'success');
                      } else {
                        showNotification('Failed to enable notifications. Please check your browser settings.', 'error');
                      }
                    } catch (error) {
                      console.error('Error enabling notifications:', error);
                      showNotification('Failed to enable notifications. Please try again.', 'error');
                    }
                  }}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
                >
                  <Bell className="w-4 h-4" />
                  Enable Notifications
                </button>
                <button
                  onClick={() => setShowNotificationBanner(false)}
                  className="px-4 py-2 bg-white text-yellow-600 border border-yellow-600 rounded-lg hover:bg-yellow-50 transition-colors text-sm font-medium"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Free Plan Upgrade Banner */}
      {isFreePlan && !isSubscriptionCancelled && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Sparkles className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Upgrade to Access Home Dashboard</h3>
              <p className="text-sm text-blue-800 mb-4">
                You're currently on the <strong>Communicate (Free)</strong> plan. This page will be empty because compliance tasks, metrics, and scorecards are only available on the <strong>Operate</strong> or <strong>Comply</strong> plans. Upgrade your plan to start using these features.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Upgrade Plan
                </button>
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-white text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                >
                  View Plans
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operate Plan Educational Banner */}
      {isOperatePlan && !isSubscriptionCancelled && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Info className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Operate Plan - Custom Tasks Only</h3>
              <p className="text-sm text-green-800 mb-4">
                You're currently on the <strong>Operate</strong> plan. This page will show custom tasks created from forms, checklists, or using the "Add Task" button. However, <strong>compliance tasks will not be automatically generated</strong> from your compliance policies. Upgrade to the <strong>Comply</strong> plan to enable automatic compliance task generation.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Upgrade to Comply Plan
                </button>
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-white text-green-600 border border-green-600 rounded-lg hover:bg-green-50 transition-colors text-sm font-medium"
                >
                  View Plans
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {isLoadingMetrics ? (
          <>
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
          </>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowHighRiskModal(true)}>
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-6 h-6 text-gray-600" />
                <div className="text-sm font-medium text-gray-700">Due Soon</div>
              </div>
              <div className="text-3xl font-bold text-gray-900 transition-all duration-300">{dueSoonCount}</div>
              <div className="text-sm text-gray-500">Next 30 days</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
                <div className="text-sm font-medium text-gray-700">Overdue</div>
              </div>
              <div className="text-3xl font-bold text-gray-900 transition-all duration-300">{overdueCount}</div>
              <div className="text-sm text-gray-500">Needs attention</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div className="text-sm font-medium text-gray-700">On-Time Rate</div>
              </div>
              <div className="text-3xl font-bold text-gray-900 transition-all duration-300">{onTimeRate}%</div>
              <div className="text-sm text-gray-500">Last 90 days</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <FileCheck className="w-6 h-6 text-orange-600" />
                <div className="text-sm font-medium text-gray-700">Pending Reviews</div>
              </div>
              <div className="text-3xl font-bold text-gray-900 transition-all duration-300">{pendingReviewCount}</div>
              <div className="text-sm text-gray-500">Awaiting review</div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <Bell className="w-6 h-6 text-gray-600" />
                <div className="text-sm font-medium text-gray-700">High Risk Attention</div>
              </div>
              <div className="text-3xl font-bold text-gray-900 transition-all duration-300">{highRiskDueSoonCount}</div>
              <div className="text-sm text-gray-500">Due soon or overdue</div>
            </div>
          </>
        )}
      </div>

      {/* Charts Row */}
      <div className={`grid gap-4 ${
        orgTypeConfig.homePage.showLocationScorecards && orgTypeConfig.homePage.showCompanyMetrics
          ? 'grid-cols-1 md:grid-cols-2'
          : 'grid-cols-1'
      }`}>
        {/* Compact Compliance Scorecard by Property */}
        {orgTypeConfig.homePage.showLocationScorecards && (
          <>
            {isLoadingMetrics ? (
              <ScorecardSkeleton />
            ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {(() => {
              // Locations for scorecard: all tasks at user’s locations (same basis as compliance score)
              const allLocations = Array.from(new Set(tasksForComplianceScorecard.map(t => t.locationName)));
              
              if (allLocations.length === 0) {
                return (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Compliance Scorecard
                      </h3>
                    </div>
                    <div className="py-8 px-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <Building2 className="w-8 h-8 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base font-semibold text-gray-900 mb-2">
                              Get Started with Compliance Scorecard
                            </h4>
                            <p className="text-sm text-gray-700 mb-4">
                              The Compliance Scorecard helps you track and monitor compliance performance across all your locations at a glance. Here's how to get started:
                            </p>
                            <div className="space-y-3 text-sm text-gray-700">
                              <div className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="font-medium">1. Create Compliance Templates</p>
                                  <p className="text-xs text-gray-600 mt-0.5">
                                    Go to <span className="font-mono bg-blue-100 px-1 rounded">Compliance Management</span> to create compliance policies and requirements that need to be tracked.
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="font-medium">2. Assign Templates to Locations</p>
                                  <p className="text-xs text-gray-600 mt-0.5">
                                    Assign your compliance templates to specific locations where they need to be tracked and completed.
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="font-medium">3. Complete Compliance Tasks</p>
                                  <p className="text-xs text-gray-600 mt-0.5">
                                    As your team completes compliance tasks, the scorecard will automatically calculate scores based on completion rates and timeliness.
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="mt-5 pt-4 border-t border-blue-200">
                              <p className="text-xs font-medium text-gray-900 mb-1">How This Helps:</p>
                              <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                                <li>Monitor compliance health across all locations in real-time</li>
                                <li>Identify locations that need attention before issues arise</li>
                                <li>Track trends and improvement over time</li>
                                <li>Make data-driven decisions to improve compliance performance</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                );
              }

            // Sort locations by score (descending)
            const locationsWithScores = allLocations
              .map(locationName => ({
                name: locationName,
                score: locationScores.get(locationName) ?? 100 // Default to 100 if no score calculated yet
              }))
              .sort((a, b) => b.score - a.score);

            const MAX_LOCATIONS_INLINE = 5;
            const displayLocations = isScorecardExpanded 
              ? locationsWithScores 
              : locationsWithScores.slice(0, MAX_LOCATIONS_INLINE);
            const hasMoreLocations = locationsWithScores.length > MAX_LOCATIONS_INLINE;

            return (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Compliance Scorecard
                  </h3>
                  {locationsWithScores.length > 0 && (
                    <button
                      onClick={() => setShowScorecardModal(true)}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                      title="View all locations in expanded view"
                    >
                      <Maximize2 className="w-4 h-4" />
                      View All ({locationsWithScores.length} location{locationsWithScores.length !== 1 ? 's' : ''})
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                {displayLocations.map(({ name, score }) => (
                  <div
                    key={name}
                    onClick={() => setSelectedLocationForDetails(name)}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                    title="Click to view compliance score details"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Building2 className="w-4 h-4 text-gray-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 truncate" title={name}>
                        {name}
                      </span>
                    </div>
                    <div className={`px-3 py-1 rounded border text-sm font-semibold flex-shrink-0 ${getScoreBadgeClass(score)}`}>
                      {score.toFixed(1)}%
                    </div>
                  </div>
                ))}
                {hasMoreLocations && (
                  <button
                    onClick={() => setIsScorecardExpanded(!isScorecardExpanded)}
                    className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors"
                  >
                    {isScorecardExpanded ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Show More ({locationsWithScores.length - MAX_LOCATIONS_INLINE} more)
                      </>
                    )}
                  </button>
                )}
              </div>
              </>
            );
          })()}
          </div>
        )}
          </>
        )}

        {/* Site Metrics Table */}
        {orgTypeConfig.homePage.showCompanyMetrics && (
          <>
            {isLoadingMetrics ? (
              <SiteMetricsSkeleton />
            ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Site Metrics</h3>
              {(() => {
                // Calculate total locations for the button
                const locationMetrics: Record<string, { dueSoon: number; pastDue: number; highRisk: number; pendingReview: number }> = {};
                const activeTasks = tasks.filter(task => task.status !== 'completed');
                const now = new Date();
                
                activeTasks.forEach(task => {
                  if (!locationMetrics[task.locationName]) {
                    locationMetrics[task.locationName] = { dueSoon: 0, pastDue: 0, highRisk: 0, pendingReview: 0 };
                  }
                  
                  const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
                  const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                  
                  if (task.status === 'pending' && daysUntilDue >= 0 && daysUntilDue <= 30) {
                    locationMetrics[task.locationName].dueSoon++;
                  }
                  if (task.status === 'overdue') {
                    locationMetrics[task.locationName].pastDue++;
                  }
                  if (task.status === 'pending_review') {
                    locationMetrics[task.locationName].pendingReview++;
                  }
                  if (isHighRiskDueSoonOrOverdue(task)) {
                    locationMetrics[task.locationName].highRisk++;
                  }
                });
                
                const totalLocations = Object.keys(locationMetrics).length;
                
                return totalLocations > 0 ? (
                  <button
                    onClick={() => setShowSiteMetricsModal(true)}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                    title="View all locations in expanded view"
                  >
                    <Maximize2 className="w-4 h-4" />
                    View All ({totalLocations} location{totalLocations !== 1 ? 's' : ''})
                  </button>
                ) : null;
              })()}
            </div>
            {(() => {
              // Calculate metrics per location
              const locationMetrics: Record<string, { dueSoon: number; pastDue: number; highRisk: number; pendingReview: number }> = {};
              
              // Filter out completed tasks first
              const activeTasks = tasks.filter(task => task.status !== 'completed');
              
              const now = new Date();
              
              activeTasks.forEach(task => {
                if (!locationMetrics[task.locationName]) {
                  locationMetrics[task.locationName] = { dueSoon: 0, pastDue: 0, highRisk: 0, pendingReview: 0 };
                }
                
                const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
                const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                
                // Count due soon items (pending tasks due within next 30 days)
                if (task.status === 'pending' && daysUntilDue >= 0 && daysUntilDue <= 30) {
                  locationMetrics[task.locationName].dueSoon++;
                }
                
                // Count past due items
                if (task.status === 'overdue') {
                  locationMetrics[task.locationName].pastDue++;
                }
                
                // Count pending review items
                if (task.status === 'pending_review') {
                  locationMetrics[task.locationName].pendingReview++;
                }
                
                if (isHighRiskDueSoonOrOverdue(task)) {
                  locationMetrics[task.locationName].highRisk++;
                }
              });

              // Sort locations by due soon items
              const allLocations = Object.entries(locationMetrics)
                .sort((a, b) => b[1].dueSoon - a[1].dueSoon);
              
              // Show educational message if no locations have metrics
              if (allLocations.length === 0) {
                return (
                  <div className="py-8 px-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-2xl mx-auto">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <FileCheck className="w-8 h-8 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">
                            Get Started with Site Metrics
                          </h4>
                          <p className="text-sm text-gray-700 mb-4">
                            Site Metrics provides a real-time overview of task status across all your locations, helping you stay on top of deadlines and priorities. Here's how to get started:
                          </p>
                          <div className="space-y-3 text-sm text-gray-700">
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium">1. Create Compliance Templates</p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                  Go to <span className="font-mono bg-green-100 px-1 rounded">Compliance Management</span> to create compliance policies that will generate tasks for your locations.
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium">2. Assign Templates to Locations</p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                  Assign compliance templates to specific locations. The system will automatically generate tasks based on the template schedule.
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium">3. Tasks Will Appear Automatically</p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                  As tasks are created and assigned, the Site Metrics table will populate with data showing Due Soon, Past Due, High Risk, and Pending Review counts per location.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="mt-5 pt-4 border-t border-green-200">
                            <p className="text-xs font-medium text-gray-900 mb-1">How This Helps:</p>
                            <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                              <li>Monitor task status across all locations at a glance</li>
                              <li>Identify locations with tasks due soon (within 30 days)</li>
                              <li>Track past due tasks that need immediate attention</li>
                              <li>Spot high-risk tasks that require urgent action</li>
                              <li>Manage tasks pending review to ensure quality control</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              
              // Show table if there are locations with metrics
              return (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Soon</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Past Due</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">High Risk</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Review</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        const displayLocations = isSiteMetricsExpanded 
                          ? allLocations 
                          : allLocations.slice(0, 5);
                        const hasMoreLocations = allLocations.length > 5;

                        return (
                    <>
                      {displayLocations.map(([locationName, metrics]) => (
                    <tr key={locationName} className="hover:bg-gray-50">
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {locationName}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700">
                        {metrics.dueSoon}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700">
                        {metrics.pastDue}
                      </td>
                      <td 
                        className="px-3 py-3 whitespace-nowrap"
                      >
                        <button
                          onClick={() => {
                            setSelectedLocation(locationName);
                            setShowHighRiskModal(true);
                          }}
                          className={`text-sm font-semibold ${
                            metrics.highRisk > 0 
                              ? 'text-red-600 hover:text-red-700 hover:underline cursor-pointer' 
                              : 'text-gray-400 cursor-default'
                          }`}
                          disabled={metrics.highRisk === 0}
                        >
                          {metrics.highRisk}
                        </button>
                      </td>
                      <td 
                        className="px-3 py-3 whitespace-nowrap"
                      >
                        <button
                          onClick={() => {
                            setSelectedLocation(locationName);
                            setShowPendingReviewModal(true);
                          }}
                          className={`text-sm font-semibold ${
                            metrics.pendingReview > 0 
                              ? 'text-orange-600 hover:text-orange-700 hover:underline cursor-pointer' 
                              : 'text-gray-400 cursor-default'
                          }`}
                          disabled={metrics.pendingReview === 0}
                        >
                          {metrics.pendingReview}
                        </button>
                      </td>
                    </tr>
                      ))}
                      {hasMoreLocations && (
                        <tr>
                          <td colSpan={5} className="px-3 py-3 text-center">
                            <button
                              onClick={() => setIsSiteMetricsExpanded(!isSiteMetricsExpanded)}
                              className="flex items-center justify-center gap-2 mx-auto py-2 px-4 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors"
                            >
                              {isSiteMetricsExpanded ? (
                                <>
                                  <ChevronUp className="w-4 h-4" />
                                  Show Less ({allLocations.length - 5} hidden)
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-4 h-4" />
                                  Show All ({allLocations.length - 5} more)
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                      })()}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}
          </>
        )}
      </div>

      {/* Compliance Review Section - For Bank organizations */}
      {orgTypeConfig.homePage.showComplianceReview && (
        <div className="mb-6 rounded-xl border border-slate-200/90 bg-gradient-to-br from-slate-50/90 via-white to-indigo-50/30 shadow-sm overflow-hidden">
          <div className="border-b border-slate-200/80 bg-white/70 backdrop-blur-sm px-6 py-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm">
                      <FileCheck className="w-5 h-5" />
                    </span>
                    Loan file compliance review
                  </h3>
                  <span
                    className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900"
                    title="Metrics below are static demo data for presentations"
                  >
                    Demo metrics
                  </span>
                </div>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">
                  Portfolio QC status, fair lending checkpoints, and automated file review coverage—illustrative figures until live analytics are connected.
                </p>
              </div>
              <button
                type="button"
                onClick={loadComplianceReviewData}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 disabled:opacity-50"
                disabled={isLoadingComplianceReview}
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingComplianceReview ? 'animate-spin' : ''}`} />
                {isLoadingComplianceReview ? 'Loading…' : 'Refresh loan files'}
              </button>
            </div>

            {/* Demo KPI strip */}
            <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-6">
              <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-100">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active loan files</span>
                  <Package className="h-4 w-4 text-indigo-500" aria-hidden />
                </div>
                <p className="mt-2 text-2xl font-bold tabular-nums text-slate-900">
                  {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.activeLoanFiles.toLocaleString()}
                </p>
                <p className="mt-1 text-xs text-slate-500">In retail mortgage QC scope</p>
              </div>
              <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-100">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">QC pass rate</span>
                  <BarChart3 className="h-4 w-4 text-emerald-600" aria-hidden />
                </div>
                <p className="mt-2 text-2xl font-bold tabular-nums text-emerald-700">
                  {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.passRateLast30d}%
                </p>
                <p className="mt-1 text-xs text-slate-500">Rolling 30 days</p>
              </div>
              <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-4 shadow-sm ring-1 ring-amber-100">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-amber-900/80">Needs reviewer</span>
                  <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
                </div>
                <p className="mt-2 text-2xl font-bold tabular-nums text-amber-900">
                  {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.needsReview}
                </p>
                <p className="mt-1 text-xs text-amber-800/80">Open NEEDS_REVIEW queue</p>
              </div>
              <div className="rounded-xl border border-red-200/80 bg-red-50/40 p-4 shadow-sm ring-1 ring-red-100">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-red-900/80">Exceptions</span>
                  <XCircle className="h-4 w-4 text-red-600" aria-hidden />
                </div>
                <p className="mt-2 text-2xl font-bold tabular-nums text-red-800">
                  {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.failedExceptions}
                </p>
                <p className="mt-1 text-xs text-red-800/80">
                  {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.exceptionsTrendVsPrior > 0 ? '+' : ''}
                  {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.exceptionsTrendVsPrior}% vs prior month
                </p>
              </div>
              <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-100">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Avg. days to clear</span>
                  <Timer className="h-4 w-4 text-slate-500" aria-hidden />
                </div>
                <p className="mt-2 text-2xl font-bold tabular-nums text-slate-900">
                  {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.avgDaysToClear}
                </p>
                <p className="mt-1 text-xs text-slate-500">Reviewer cycle time</p>
              </div>
              <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-100">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Auto-run coverage</span>
                  <Shield className="h-4 w-4 text-indigo-600" aria-hidden />
                </div>
                <p className="mt-2 text-2xl font-bold tabular-nums text-indigo-800">
                  {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.autoRunCoveragePct}%
                </p>
                <p className="mt-1 text-xs text-slate-500">Files with completed auto QC</p>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-slate-200/80 bg-white/90 px-4 py-3">
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <UserCheck className="h-4 w-4 text-indigo-600" aria-hidden />
                  <span className="tabular-nums">{BANK_COMPLIANCE_REVIEW_DEMO_KPIS.filesReviewedMTD}</span>
                  <span className="text-slate-500">files cleared MTD</span>
                </span>
                <span className="hidden sm:inline text-slate-300" aria-hidden>
                  |
                </span>
                <span className="text-slate-600">
                  <span className="font-semibold tabular-nums text-slate-900">
                    {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.pendingInitialRun}
                  </span>{' '}
                  awaiting first checklist run
                </span>
              </div>
              <div className="flex flex-1 min-w-[min(100%,12rem)] max-w-md flex-col gap-1">
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Portfolio mix (demo)</span>
                  <span className="tabular-nums">
                    Pass {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.passedQC} · Review {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.needsReview} ·
                    Pending {BANK_COMPLIANCE_REVIEW_DEMO_KPIS.pendingInitialRun}
                  </span>
                </div>
                <div
                  className="flex h-2.5 overflow-hidden rounded-full bg-slate-100"
                  role="img"
                  aria-label="Demo portfolio status mix"
                >
                  <div
                    className="bg-emerald-500"
                    style={{
                      width: `${(BANK_COMPLIANCE_REVIEW_DEMO_KPIS.passedQC / BANK_COMPLIANCE_REVIEW_DEMO_KPIS.activeLoanFiles) * 100}%`,
                    }}
                  />
                  <div
                    className="bg-amber-400"
                    style={{
                      width: `${(BANK_COMPLIANCE_REVIEW_DEMO_KPIS.needsReview / BANK_COMPLIANCE_REVIEW_DEMO_KPIS.activeLoanFiles) * 100}%`,
                    }}
                  />
                  <div
                    className="bg-red-500"
                    style={{
                      width: `${(BANK_COMPLIANCE_REVIEW_DEMO_KPIS.failedExceptions / BANK_COMPLIANCE_REVIEW_DEMO_KPIS.activeLoanFiles) * 100}%`,
                    }}
                  />
                  <div
                    className="bg-slate-300"
                    style={{
                      width: `${(BANK_COMPLIANCE_REVIEW_DEMO_KPIS.pendingInitialRun / BANK_COMPLIANCE_REVIEW_DEMO_KPIS.activeLoanFiles) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 pt-5">
          {(() => {
            // Find loan officer field in asset types
            const loanOfficerField = assetTypes
              .flatMap(at => at.customFields || [])
              .find(cf => cf.type === 'loan_officer_picker');
            
            // Compute filtered assets
            const filteredAssets = assets.filter((asset) => {
              // Filter by asset name
              if (assetNameFilter && !asset.assetName?.toLowerCase().includes(assetNameFilter.toLowerCase())) {
                return false;
              }
              // Filter by asset type
              if (assetTypeFilter !== 'all' && asset.assetTypeId !== assetTypeFilter) {
                return false;
              }
              
              // Filter by loan officer (for managers)
              if (loanOfficerField && loanOfficerFilter !== 'all' && isUserManager) {
                const assignedLoanOfficerId = asset.fieldValues?.[loanOfficerField.name];
                if (assignedLoanOfficerId !== loanOfficerFilter) {
                  return false;
                }
              }
              
              // Auto-filter for loan officers (non-managers)
              if (loanOfficerField && !isUserManager && user?.id) {
                const assignedLoanOfficerId = asset.fieldValues?.[loanOfficerField.name];
                if (assignedLoanOfficerId !== user.id) {
                  return false;
                }
              }
              
              return true;
            });

            return isLoadingComplianceReview ? (
              <div className="text-center py-8 text-gray-500">Loading compliance data...</div>
            ) : assets.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-10 text-center">
                <Package className="mx-auto h-10 w-10 text-slate-300" aria-hidden />
                <p className="mt-3 text-sm font-medium text-slate-800">No loan files in workspace yet</p>
                <p className="mt-1 text-sm text-slate-500">
                  Create loan file assets to run checklists and see live status alongside the demo metrics above.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Filters */}
                <div className="flex flex-wrap items-center gap-4 pb-4 border-b border-gray-200">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filter by Asset Name
                    </label>
                    <input
                      type="text"
                      value={assetNameFilter}
                      onChange={(e) => setAssetNameFilter(e.target.value)}
                      placeholder="Search asset name..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filter by Asset Type
                    </label>
                    <select
                      value={assetTypeFilter}
                      onChange={(e) => setAssetTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Asset Types</option>
                      {assetTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {(() => {
                    // Find loan officer field in asset types
                    const loanOfficerField = assetTypes
                      .flatMap(at => at.customFields || [])
                      .find(cf => cf.type === 'loan_officer_picker');
                    
                    return (
                      <>
                        {loanOfficerField && isUserManager && (
                          <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Filter by {loanOfficerField.name}
                            </label>
                            <select
                              value={loanOfficerFilter}
                              onChange={(e) => setLoanOfficerFilter(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="all">All {loanOfficerField.name}s</option>
                              {eligibleLoanOfficers.map(lo => (
                                <option key={lo.id} value={lo.id}>{lo.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {(assetNameFilter || assetTypeFilter !== 'all' || (loanOfficerFilter !== 'all' && loanOfficerField)) && (
                          <div className="flex items-end">
                            <button
                              onClick={() => {
                                setAssetNameFilter('');
                                setAssetTypeFilter('all');
                                setLoanOfficerFilter('all');
                              }}
                              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:underline"
                            >
                              Clear Filters
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {filteredAssets.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No assets match the current filters. Try adjusting your search criteria.
                  </div>
                ) : (
                  /* Assets Table */
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Asset Name
                          </th>
                          {(() => {
                            // Find loan officer field in asset types
                            const loanOfficerField = assetTypes
                              .flatMap(at => at.customFields || [])
                              .find(cf => cf.type === 'loan_officer_picker');
                            
                            return loanOfficerField && isUserManager ? (
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {loanOfficerField.name}
                              </th>
                            ) : null;
                          })()}
                          {(() => {
                            // Find loan number field in asset types
                            const loanNumberField = assetTypes
                              .flatMap(at => at.customFields || [])
                              .find(cf => cf.type === 'alphanumeric');
                            
                            return loanNumberField ? (
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {loanNumberField.name}
                              </th>
                            ) : null;
                          })()}
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredAssets.map((asset) => {
                      const completions = assetCompletions[asset.id || ''] || [];
                      const latestCompletion = completions.length > 0 
                        ? completions.sort((a, b) => {
                            const aTime = a.completedAt instanceof Date ? a.completedAt.getTime() : 
                                         a.completedAt?.toDate ? a.completedAt.toDate().getTime() : 
                                         typeof a.completedAt === 'string' ? new Date(a.completedAt).getTime() : 0;
                            const bTime = b.completedAt instanceof Date ? b.completedAt.getTime() : 
                                         b.completedAt?.toDate ? b.completedAt.toDate().getTime() : 
                                         typeof b.completedAt === 'string' ? new Date(b.completedAt).getTime() : 0;
                            return bTime - aTime;
                          })[0]
                        : null;
                      
                      // Calculate auto-run status
                      const autoRunCompletions = completions.filter(c => 
                        c.completedByName === 'AUTO' || c.completedBy === 'AUTO' || 
                        (typeof c.completedBy === 'string' && c.completedBy.includes('AUTO'))
                      );
                      const hasAutoRun = autoRunCompletions.length > 0;
                      const autoRunStatus = hasAutoRun 
                        ? (autoRunCompletions.some(c => c.status === 'complete') ? 'Complete' : 'Paused')
                        : 'Not Run';
                      
                      const isExpanded = expandedAssets.has(asset.id || '');
                      const itemResults = latestCompletion?.itemResults || {};
                      const itemKeys = Object.keys(itemResults);
                      
                      // Calculate overall checklist status
                      let overallStatus: 'PASS' | 'PENDING' | 'NEEDS_REVIEW' | 'FAIL' = 'PENDING';
                      if (itemKeys.length > 0) {
                        const statuses = itemKeys.map(key => {
                          const itemResult = itemResults[key] as any;
                          return itemResult?.status || 'PENDING';
                        });
                        
                        // If all items are PASS or NA, status is PASS
                        if (statuses.every(s => s === 'PASS' || s === 'NA')) {
                          overallStatus = 'PASS';
                        } 
                        // If any item is PENDING, status is PENDING
                        else if (statuses.some(s => s === 'PENDING')) {
                          overallStatus = 'PENDING';
                        }
                        // If any item is NEEDS_REVIEW, status is NEEDS_REVIEW
                        else if (statuses.some(s => s === 'NEEDS_REVIEW')) {
                          overallStatus = 'NEEDS_REVIEW';
                        }
                        // If any item is FAIL, status is FAIL
                        else if (statuses.some(s => s === 'FAIL')) {
                          overallStatus = 'FAIL';
                        }
                      }
                      
                      // Find loan officer field in asset types
                      const loanOfficerField = assetTypes
                        .flatMap(at => at.customFields || [])
                        .find(cf => cf.type === 'loan_officer_picker');
                      
                      const assignedLoanOfficerId = loanOfficerField 
                        ? asset.fieldValues?.[loanOfficerField.name] 
                        : undefined;
                      const assignedLoanOfficer = assignedLoanOfficerId 
                        ? eligibleLoanOfficers.find(lo => lo.id === assignedLoanOfficerId)
                        : undefined;
                      
                      // Find loan number field in asset types
                      const loanNumberField = assetTypes
                        .flatMap(at => at.customFields || [])
                        .find(cf => cf.type === 'alphanumeric');
                      
                      const loanNumber = loanNumberField 
                        ? asset.fieldValues?.[loanNumberField.name] 
                        : undefined;
                      
                      return (
                        <React.Fragment key={asset.id}>
                          <tr 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={(e) => {
                              // Don't expand if clicking on buttons or links
                              const target = e.target as HTMLElement;
                              if (target.closest('button') || target.closest('a')) {
                                return;
                              }
                              toggleAssetExpansion(asset.id || '');
                            }}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="p-1">
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                  )}
                                </div>
                                <Package className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">
                                  {asset.assetName || 'Unnamed Asset'}
                                </span>
                              </div>
                            </td>
                            {loanOfficerField && isUserManager && (
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                {assignedLoanOfficer ? assignedLoanOfficer.name : 'Unassigned'}
                              </td>
                            )}
                            {loanNumberField && (
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                {loanNumber || '—'}
                              </td>
                            )}
                            <td className="px-4 py-3 whitespace-nowrap">
                              {latestCompletion && itemKeys.length > 0 ? (
                                <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                  overallStatus === 'PASS'
                                    ? 'bg-green-100 text-green-800 border border-green-300'
                                    : overallStatus === 'PENDING'
                                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                                    : overallStatus === 'NEEDS_REVIEW'
                                    ? 'bg-orange-100 text-orange-800 border border-orange-300'
                                    : 'bg-red-100 text-red-800 border border-red-300'
                                }`}>
                                  {overallStatus === 'PASS' && '✓ Pass'}
                                  {overallStatus === 'PENDING' && '⏳ Pending'}
                                  {overallStatus === 'NEEDS_REVIEW' && '⚠ Needs Review'}
                                  {overallStatus === 'FAIL' && '✗ Fail'}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <div className="flex items-center gap-2">
                                {latestCompletion && (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewDetails(asset, latestCompletion);
                                      }}
                                      className="text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                      View Details
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleExportComplianceReport(asset, latestCompletion);
                                      }}
                                      className="text-green-600 hover:text-green-800 hover:underline flex items-center gap-1"
                                      title="Export Compliance Review Report"
                                    >
                                      <Download className="w-4 h-4" />
                                      Export
                                    </button>
                                  </>
                                )}
                                <a
                                  href={`${window.location.origin}/asset/${asset.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-blue-600 hover:text-blue-800 hover:underline underline cursor-pointer"
                                >
                                  Asset Record
                                </a>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && latestCompletion && (
                            <tr>
                              <td colSpan={(() => {
                                // Find loan officer field in asset types
                                const loanOfficerField = assetTypes
                                  .flatMap(at => at.customFields || [])
                                  .find(cf => cf.type === 'loan_officer_picker');
                                // Find loan number field in asset types
                                const loanNumberField = assetTypes
                                  .flatMap(at => at.customFields || [])
                                  .find(cf => cf.type === 'alphanumeric');
                                
                                let colCount = 3; // Asset Name, Status, Actions
                                if (loanOfficerField && isUserManager) colCount++;
                                if (loanNumberField) colCount++;
                                
                                return colCount;
                              })()} className="px-4 py-3 bg-gray-50">
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs font-semibold text-gray-700">
                                      Checklist Items ({itemKeys.length} total)
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`px-3 py-1 text-xs font-semibold rounded ${
                                        overallStatus === 'PASS'
                                          ? 'bg-green-100 text-green-800 border border-green-300'
                                          : overallStatus === 'PENDING'
                                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                                          : overallStatus === 'NEEDS_REVIEW'
                                          ? 'bg-orange-100 text-orange-800 border border-orange-300'
                                          : 'bg-red-100 text-red-800 border border-red-300'
                                      }`}>
                                        {overallStatus === 'PASS' && '✓ Pass'}
                                        {overallStatus === 'PENDING' && '⏳ Pending'}
                                        {overallStatus === 'NEEDS_REVIEW' && '⚠ Needs Review'}
                                        {overallStatus === 'FAIL' && '✗ Fail'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                                    {itemKeys.map((itemKey) => {
                                      const itemResult = itemResults[itemKey] as any;
                                      const status = itemResult?.status || 'PENDING';
                                      const findingText = itemResult?.findingText || '';
                                      const statusColors = {
                                        'PASS': 'bg-green-100 text-green-800',
                                        'FAIL': 'bg-red-100 text-red-800',
                                        'NA': 'bg-gray-100 text-gray-800',
                                        'NEEDS_REVIEW': 'bg-yellow-100 text-yellow-800',
                                        'PENDING': 'bg-blue-100 text-blue-800'
                                      };
                                      return (
                                        <div key={itemKey} className="flex items-start gap-2 text-xs p-2 border border-gray-200 rounded hover:bg-gray-50">
                                          <span className={`px-2 py-1 rounded font-medium flex-shrink-0 ${statusColors[status as keyof typeof statusColors] || statusColors.PENDING}`}>
                                            {status}
                                          </span>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-gray-700 font-medium truncate">{itemKey}</div>
                                            {findingText && (
                                              <div className="text-gray-500 text-xs mt-1 line-clamp-2">{findingText}</div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <button
                                    onClick={() => handleViewDetails(asset, latestCompletion)}
                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-2"
                                  >
                                    View full checklist run details →
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}
          </div>
        </div>
      )}

      {/* Checklist Run Details Side Panel */}
      {selectedAssetForDetails && selectedCompletionForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
          <div className="bg-white w-full max-w-4xl h-full overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Checklist Run Details
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedAssetForDetails.assetName || 'Unnamed Asset'}
                </p>
              </div>
              <button
                onClick={handleCloseDetails}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {isLoadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500">Loading details...</div>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Checklist Items Section */}
                {checklistDetails && (() => {
                  const bankRunFilter = orgTypeConfig.homePage.showChecklistRunActionableFilter === true;
                  const runDetailsItems =
                    bankRunFilter && checklistRunDetailsViewMode === 'actionable'
                      ? (checklistDetails.items || []).filter((it) =>
                          isActionableChecklistItemStatus(
                            selectedCompletionForDetails.itemResults?.[it.text]?.status
                          )
                        )
                      : checklistDetails.items || [];
                  return (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <ClipboardList className="w-5 h-5" />
                      Checklist Items
                    </h3>
                    {bankRunFilter && (
                      <div className="mb-4 flex items-center justify-between gap-3 pb-4 border-b border-gray-200">
                        <span className="text-sm text-gray-800">
                          Only need review / failed items & fields that need review
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={checklistRunDetailsViewMode === 'actionable'}
                          aria-label="Show only actionable items and fields"
                          onClick={() =>
                            setChecklistRunDetailsViewMode((m) =>
                              m === 'actionable' ? 'all' : 'actionable'
                            )
                          }
                          className={`relative inline-block h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            checklistRunDetailsViewMode === 'actionable'
                              ? 'bg-blue-600'
                              : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-1 left-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                              checklistRunDetailsViewMode === 'actionable'
                                ? 'translate-x-5'
                                : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    )}
                    {runDetailsItems.length === 0 && bankRunFilter && checklistRunDetailsViewMode === 'actionable' ? (
                      <div className="text-sm text-gray-600 py-4">
                        No items need review or failed for this run.
                      </div>
                    ) : (
                    <div className="space-y-2">
                      {runDetailsItems.map((item, index) => {
                        const actionableRunView =
                          bankRunFilter && checklistRunDetailsViewMode === 'actionable';
                        const itemResult = selectedCompletionForDetails.itemResults?.[item.text] as any;
                        const status = itemResult?.status || 'PENDING';
                        const findingText = itemResult?.findingText || '';
                        const isExpanded = expandedChecklistItems.has(item.text);
                        const evidence = itemEvidenceMap[item.text] || [];
                        
                        // Status colors matching the image style
                        const statusColors = {
                          'PASS': 'bg-green-100 text-green-800 border-green-300',
                          'FAIL': 'bg-red-100 text-red-800 border-red-300',
                          'NA': 'bg-gray-100 text-gray-800 border-gray-300',
                          'NEEDS_REVIEW': 'bg-yellow-100 text-yellow-800 border-yellow-300',
                          'PENDING': 'bg-blue-100 text-blue-800 border-blue-300'
                        };
                        
                        const statusBgColors = {
                          'PASS': 'bg-green-50',
                          'FAIL': 'bg-red-50',
                          'NA': 'bg-gray-50',
                          'NEEDS_REVIEW': 'bg-yellow-50',
                          'PENDING': 'bg-blue-50'
                        };
                        
                        const toggleExpand = async () => {
                          const newExpanded = new Set(expandedChecklistItems);
                          if (newExpanded.has(item.text)) {
                            newExpanded.delete(item.text);
                          } else {
                            newExpanded.add(item.text);
                            // Load evidence if not already loaded
                            if (!itemEvidenceMap[item.text] && selectedCompletionForDetails.id) {
                              try {
                                const evidenceResult = await checklistEvidenceService.getEvidenceByItemResult(
                                  selectedCompletionForDetails.id,
                                  item.text
                                );
                                if (evidenceResult.success && evidenceResult.data) {
                                  // Check if any evidence records are missing validationStatus and need re-validation
                                  const evidenceNeedsValidation = evidenceResult.data.some(ev => 
                                    !ev.validationStatus && !ev.fieldStatus
                                  );
                                  
                                  if (evidenceNeedsValidation && item.evidenceDefinitions && item.evidenceDefinitions.length > 0) {
                                    // Trigger re-validation for evidence without status
                                    console.log('[HomePage] Evidence missing validation status, triggering re-validation for item:', item.text);
                                    // Re-validate will happen when user clicks "Re-evaluate" button
                                    // For now, just set the evidence map - user can click Re-evaluate to update
                                  }
                                  
                                  setItemEvidenceMap(prev => ({
                                    ...prev,
                                    [item.text]: evidenceResult.data || []
                                  }));
                                }
                              } catch (error) {
                                console.error(`Error loading evidence for item "${item.text}":`, error);
                              }
                            }
                          }
                          setExpandedChecklistItems(newExpanded);
                        };
                        
                        return (
                          <div 
                            key={`${item.text}-${index}`}
                            className={`${statusBgColors[status as keyof typeof statusBgColors] || statusBgColors.PENDING} rounded-lg border-2 ${statusColors[status as keyof typeof statusColors] || statusColors.PENDING} overflow-hidden`}
                          >
                            {/* Item Header - Clickable */}
                            <div 
                              className="p-3 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={toggleExpand}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900">
                                      {index + 1}. {item.text}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${statusColors[status as keyof typeof statusColors] || statusColors.PENDING}`}>
                                      Status: {status}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleReevaluateItem(item);
                                      }}
                                      disabled={reevaluatingItems.has(item.text)}
                                      className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 transition-colors ${
                                        reevaluatingItems.has(item.text)
                                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                      }`}
                                      title="Re-evaluate this item with current field values"
                                      aria-label={`Re-evaluate checklist item: ${item.text}`}
                                      aria-busy={reevaluatingItems.has(item.text)}
                                    >
                                      {reevaluatingItems.has(item.text) ? (
                                        <>
                                          <RefreshCw className="w-3 h-3 animate-spin" />
                                          <span>Re-evaluating...</span>
                                        </>
                                      ) : (
                                        <>
                                          <RotateCcw className="w-3 h-3" />
                                          <span>Re-evaluate</span>
                                        </>
                                      )}
                                    </button>
                                    {status === 'NEEDS_REVIEW' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMarkAsPassed(item);
                                        }}
                                        disabled={markingAsPassedItems.has(item.text)}
                                        className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 transition-colors ${
                                          markingAsPassedItems.has(item.text)
                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                                        }`}
                                        title="Mark this item as reviewed and passed"
                                        aria-label={`Mark checklist item as passed: ${item.text}`}
                                        aria-busy={markingAsPassedItems.has(item.text)}
                                      >
                                        {markingAsPassedItems.has(item.text) ? (
                                          <>
                                            <RefreshCw className="w-3 h-3 animate-spin" />
                                            <span>Updating...</span>
                                          </>
                                        ) : (
                                          <>
                                            <CheckCircle className="w-3 h-3" />
                                            <span>Mark as Passed</span>
                                          </>
                                        )}
                                      </button>
                                    )}
                                    <span className="text-gray-400 text-xs">
                                      {isExpanded ? '▼' : '▶'}
                                    </span>
                                  </div>
                                  {findingText && (
                                    <div className="text-xs text-gray-700 mt-1 italic">
                                      {findingText}
                                    </div>
                                  )}
                                  {!isExpanded && (evidence.length > 0 || (item.evidenceDefinitions && item.evidenceDefinitions.length > 0)) && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {item.evidenceDefinitions?.length ?? evidence.length} evidence item{(item.evidenceDefinitions?.length ?? evidence.length) !== 1 ? 's' : ''} • Click to expand
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Expanded Content */}
                            {isExpanded && (
                              <div className="border-t border-gray-300 p-3 bg-white space-y-3">
                                {/* Evidence Section - show all definitions, including workflow-only (no fields) */}
                                {item.evidenceDefinitions && item.evidenceDefinitions.length > 0 && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-gray-700 mb-2">Evidence ({item.evidenceDefinitions.length})</h5>
                                    <div className="space-y-2">
                                      {item.evidenceDefinitions.map((evDef, evDefIdx) => {
                                        const ev = evidence.find((e: any) =>
                                          (e.evidenceName || e.categoryCode) === (evDef.evidenceName || (evDef as any).categoryCode)
                                        );
                                        if (!ev) {
                                          // No completion record for this evidence (e.g. workflow-only evidences with no field definitions)
                                          return (
                                            <div key={evDefIdx} className="border rounded p-3 text-xs bg-gray-50 border-gray-200">
                                              <div className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                                                <span>{evDef.evidenceName || (evDef as any).categoryCode || 'Unknown Evidence'}</span>
                                                {evDef.required ? (
                                                  <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-medium">Required</span>
                                                ) : (
                                                  <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-[10px] rounded">Optional</span>
                                                )}
                                              </div>
                                              {evDef.workflowRules && (
                                                <div className="text-blue-600 text-[11px] mb-2 bg-blue-50 p-1.5 rounded border border-blue-200">
                                                  <strong>Workflow Rules:</strong> {evDef.workflowRules}
                                                </div>
                                              )}
                                              <div className="text-gray-500 text-[11px] italic">No fields — evaluated by workflow rules</div>
                                            </div>
                                          );
                                        }
                                        if (actionableRunView && ev && !checklistEvidenceNeedsAttention(ev)) {
                                          return null;
                                        }
                                        const evIdx = evDefIdx;
                                        // Determine validation status - check if fieldStatus exists to calculate status
                                        let validationStatus = ev.validationStatus;
                                        if (!validationStatus) {
                                          if (ev.fieldStatus) {
                                            // Calculate status from fieldStatus if validationStatus is missing
                                            if (ev.fieldStatus.missingFields && ev.fieldStatus.missingFields.length > 0) {
                                              validationStatus = 'INCOMPLETE';
                                            } else if (ev.fieldStatus.invalidFields && ev.fieldStatus.invalidFields.length > 0) {
                                              validationStatus = 'INVALID';
                                            } else if (ev.fieldStatus.requiredFields > 0) {
                                              // Check if all required fields are populated and validated
                                              const requiredPopulated = ev.fieldStatus.requiredFields - (ev.fieldStatus.missingFields?.length || 0);
                                              if (requiredPopulated === ev.fieldStatus.requiredFields && 
                                                  ev.fieldStatus.validatedFields >= ev.fieldStatus.requiredFields) {
                                                validationStatus = 'COMPLETE';
                                              } else {
                                                validationStatus = 'INCOMPLETE';
                                              }
                                            } else {
                                              // No required fields, check if any fields are populated
                                              if (ev.fieldStatus.populatedFields > 0) {
                                                validationStatus = 'COMPLETE';
                                              } else {
                                                validationStatus = 'INCOMPLETE';
                                              }
                                            }
                                          } else if (ev.fieldValues) {
                                            // Try to infer from fieldValues if fieldStatus is also missing
                                            const fieldValues = ev.fieldValues;
                                            const fieldKeys = Object.keys(fieldValues);
                                            const populatedCount = fieldKeys.filter(key => {
                                              const fieldData = fieldValues[key];
                                              return fieldData && fieldData.populated;
                                            }).length;
                                            
                                            // Try to match with checklist definition to get required fields
                                            const matchingEvDef = item.evidenceDefinitions?.find(ed => 
                                              ed.evidenceName === ev.evidenceName || ed.evidenceName === (ev as any).categoryCode
                                            );
                                            
                                            if (matchingEvDef?.fieldDefinitions) {
                                              const requiredFields = matchingEvDef.fieldDefinitions.filter(f => f.required);
                                              const requiredKeys = requiredFields.map(f => f.key);
                                              const requiredPopulated = requiredKeys.filter(key => {
                                                const fieldData = fieldValues[key];
                                                return fieldData && fieldData.populated;
                                              }).length;
                                              
                                              if (requiredFields.length > 0) {
                                                if (requiredPopulated === requiredFields.length) {
                                                  // All required fields populated - check if validated
                                                  const allValidated = requiredKeys.every(key => {
                                                    const fieldData = fieldValues[key];
                                                    return fieldData && fieldData.validated !== false;
                                                  });
                                                  validationStatus = allValidated ? 'COMPLETE' : 'INCOMPLETE';
                                                } else {
                                                  validationStatus = 'INCOMPLETE';
                                                }
                                              } else if (populatedCount > 0) {
                                                validationStatus = 'COMPLETE';
                                              } else {
                                                validationStatus = 'INCOMPLETE';
                                              }
                                            } else if (populatedCount > 0) {
                                              // No definition match, but has some populated fields
                                              validationStatus = 'COMPLETE';
                                            } else {
                                              validationStatus = 'INCOMPLETE';
                                            }
                                          }
                                        }
                                        
                                        const isComplete = validationStatus === 'COMPLETE';
                                        const isIncomplete = validationStatus === 'INCOMPLETE';
                                        const isInvalid = validationStatus === 'INVALID';
                                        
                                        // Try to get evidence name from checklist definition if missing
                                        let evidenceName = ev.evidenceName;
                                        let matchingEvDef: any | undefined;
                                        if (!evidenceName) {
                                          // Check if we have categoryCode (legacy)
                                          const categoryCode = (ev as any).categoryCode;
                                          if (categoryCode) {
                                            // Try to match with checklist item's evidence definitions
                                            matchingEvDef = item.evidenceDefinitions?.find(ed => 
                                              ed.evidenceName === categoryCode || (ed as any).categoryCode === categoryCode
                                            );
                                            evidenceName = matchingEvDef?.evidenceName || categoryCode;
                                          } else {
                                            // If no categoryCode either, try to match by index or use first evidence definition
                                            if (item.evidenceDefinitions && item.evidenceDefinitions.length > evIdx) {
                                              evidenceName = item.evidenceDefinitions[evIdx].evidenceName;
                                              matchingEvDef = item.evidenceDefinitions[evIdx];
                                            }
                                          }
                                        } else {
                                          // If evidenceName exists, find matching definition
                                          matchingEvDef = item.evidenceDefinitions?.find(ed => 
                                            ed.evidenceName === evidenceName
                                          );
                                        }
                                        
                                        return (
                                          <div 
                                            key={evIdx} 
                                            className={`border rounded p-3 text-xs ${
                                              isComplete ? 'bg-green-50 border-green-300' : 
                                              isIncomplete ? 'bg-yellow-50 border-yellow-300' : 
                                              isInvalid ? 'bg-red-50 border-red-300' : 
                                              'bg-gray-50 border-gray-200'
                                            }`}
                                          >
                                            {/* Evidence Name */}
                                            <div className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                                              <span>{evidenceName || 'Unknown Evidence'}</span>
                                              {isComplete && <span className="text-green-600 text-[10px] font-semibold">✓ Complete</span>}
                                              {isIncomplete && <span className="text-yellow-600 text-[10px] font-semibold">⚠ Incomplete</span>}
                                              {isInvalid && <span className="text-red-600 text-[10px] font-semibold">✗ Invalid</span>}
                                              {!validationStatus && (
                                                <span className="text-gray-500 text-[10px] font-semibold italic">
                                                  Status Unknown (click Re-evaluate to update)
                                                </span>
                                              )}
                                            </div>
                                            
                                            {/* Evidence Description and Workflow Rules */}
                                            {matchingEvDef && (
                                              <>
                                                {matchingEvDef.description && (
                                                  <div className="text-gray-600 italic text-[11px] mb-1">
                                                    {matchingEvDef.description}
                                                  </div>
                                                )}
                                                {matchingEvDef.workflowRules && (
                                                  <div className="text-blue-600 text-[11px] mb-2 bg-blue-50 p-1.5 rounded border border-blue-200">
                                                    <strong>Workflow Rules:</strong> {matchingEvDef.workflowRules}
                                                  </div>
                                                )}
                                              </>
                                            )}
                                            
                                            {/* Field Status Summary */}
                                            {ev.fieldStatus && (
                                              <div className="text-gray-600 mb-2 text-[11px]">
                                                Fields: {ev.fieldStatus.populatedFields} / {ev.fieldStatus.totalFields} populated
                                                {ev.fieldStatus.requiredFields > 0 && (
                                                  <span className="ml-2">
                                                    ({ev.fieldStatus.populatedFields - ev.fieldStatus.missingFields.length} / {ev.fieldStatus.requiredFields} required)
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                            
                                            {/* Validation Summary */}
                                            {ev.validationSummary && (
                                              <div className={`text-[11px] mb-2 ${
                                                isComplete ? 'text-green-700' : 
                                                isIncomplete ? 'text-yellow-700' : 
                                                'text-red-700'
                                              }`}>
                                                {ev.validationSummary}
                                              </div>
                                            )}
                                            
                                            {/* Field Details (expandable) */}
                                            {ev.fieldValues && Object.keys(ev.fieldValues).length > 0 && (() => {
                                              const fieldEntries = Object.entries(ev.fieldValues).filter(
                                                ([key, fieldData]) => {
                                                  if (!actionableRunView) return true;
                                                  return (
                                                    ev.fieldStatus?.missingFields?.includes(key) ||
                                                    ev.fieldStatus?.invalidFields?.includes(key) ||
                                                    fieldData.validated === false
                                                  );
                                                }
                                              );
                                              if (actionableRunView && fieldEntries.length === 0) {
                                                return null;
                                              }
                                              return (
                                              <details className="mt-2">
                                                <summary className="cursor-pointer text-blue-600 hover:text-blue-800 text-[11px]">
                                                  View Fields ({fieldEntries.length})
                                                </summary>
                                                <div className="mt-2 space-y-1 pl-4">
                                                  {fieldEntries.map(([key, fieldData]) => {
                                                    const fieldDef = item.evidenceDefinitions
                                                      ?.flatMap(ed => ed.fieldDefinitions || [])
                                                      .find(fd => fd.key === key);
                                                    const fieldLabel = fieldDef?.label || key;
                                                    
                                                    // Check if this field is invalid
                                                    const isInvalid = ev.fieldStatus?.invalidFields?.includes(key) || fieldData.validated === false;
                                                    // Check if this field is validated
                                                    const isValidated = fieldData.validated === true;
                                                    
                                                    return (
                                                      <div key={key} className="flex items-center justify-between text-[10px]">
                                                        <span className="text-gray-700">{fieldLabel}</span>
                                                        <div className="flex items-center gap-2">
                                                          {isInvalid ? (
                                                            <span className="text-red-600">✗</span>
                                                          ) : isValidated ? (
                                                            <span className="text-green-600">✓</span>
                                                          ) : fieldData.populated ? (
                                                            <span className="text-blue-600">✓</span>
                                                          ) : (
                                                            <span className="text-gray-400">○</span>
                                                          )}
                                                          {isValidated && (
                                                            <span className="text-green-600 text-[9px]">Validated</span>
                                                          )}
                                                          {fieldData.source && (
                                                            <span className="text-gray-500 text-[9px]">from {fieldData.source}</span>
                                                          )}
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </details>
                                              );
                                            })()}
                                            
                                            {/* Missing Fields */}
                                            {ev.fieldStatus && ev.fieldStatus.missingFields && ev.fieldStatus.missingFields.length > 0 && (
                                              <div className="mt-2 text-red-600 text-[10px]">
                                                Missing: {ev.fieldStatus.missingFields.map(key => {
                                                  const fieldDef = item.evidenceDefinitions
                                                    ?.flatMap(ed => ed.fieldDefinitions || [])
                                                    .find(fd => fd.key === key);
                                                  return fieldDef?.label || key;
                                                }).join(', ')}
                                              </div>
                                            )}
                                            
                                            {/* Invalid Fields */}
                                            {ev.fieldStatus && ev.fieldStatus.invalidFields && ev.fieldStatus.invalidFields.length > 0 && (
                                              <div className="mt-2 text-red-600 text-[10px]">
                                                Invalid: {ev.fieldStatus.invalidFields.map(key => {
                                                  const fieldDef = item.evidenceDefinitions
                                                    ?.flatMap(ed => ed.fieldDefinitions || [])
                                                    .find(fd => fd.key === key);
                                                  return fieldDef?.label || key;
                                                }).join(', ')}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Fields Section */}
                                {item.evidenceDefinitions && item.evidenceDefinitions.length > 0 && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-gray-700 mb-2">Fields</h5>
                                    {actionableRunView &&
                                      !item.evidenceDefinitions.some((ed) =>
                                        (ed.fieldDefinitions || []).some((fd) =>
                                          doesChecklistFieldNeedReview(
                                            item.text,
                                            fd.key,
                                            evidence,
                                            assetFacts,
                                            fd,
                                            selectedCompletionForDetails
                                          )
                                        )
                                      ) && (
                                        <div className="text-xs text-gray-500 italic mb-2">
                                          No fields flagged for correction on this item — use Re-evaluate or Mark as
                                          Passed if needed.
                                        </div>
                                      )}
                                    <div className="space-y-2">
                                      {item.evidenceDefinitions.map((evDef, evDefIdx) => {
                                        const hasFields = evDef.fieldDefinitions && evDef.fieldDefinitions.length > 0;
                                        return (
                                        <React.Fragment key={evDefIdx}>
                                        {hasFields ? (
                                        evDef
                                          .fieldDefinitions!.filter(
                                            (fieldDef) =>
                                              !actionableRunView ||
                                              doesChecklistFieldNeedReview(
                                                item.text,
                                                fieldDef.key,
                                                evidence,
                                                assetFacts,
                                                fieldDef,
                                                selectedCompletionForDetails
                                              )
                                          )
                                          .map((fieldDef, fieldIdx) => {
                                          // Get field value from asset facts
                                          const fact = assetFacts[fieldDef.key];
                                          let fieldValue: any = null;
                                          let sourceFile = '';
                                          let confidence = 'low';
                                          
                                          const isManuallySet = fact?.source === 'REVIEWER_OVERRIDE';
                                          const overrideBy = fact?.overriddenBy;
                                          const overrideAt = fact?.overriddenAt;
                                          const overrideReason = fact?.overrideReason;
                                          
                                          if (fact) {
                                            // Priority: REVIEWER_OVERRIDE > extractedValues > fact.value
                                            if (fact.source === 'REVIEWER_OVERRIDE' && fact.value !== null && fact.value !== undefined && fact.value !== '') {
                                              // Reviewer override takes highest priority
                                              fieldValue = fact.value;
                                              sourceFile = fact.extractedFrom || fact.overrideReason?.match(/from file:\s*([^\n]+)/i)?.[1] || '';
                                              confidence = 'high'; // Reviewer overrides are always high confidence
                                            } else if (fact.extractedValues && fact.extractedValues.length > 0) {
                                              // Use primary value (highest confidence)
                                              const sorted = fact.extractedValues.sort((a: any, b: any) => {
                                                const confMap: Record<string, number> = { high: 3, medium: 2, low: 1 };
                                                return (confMap[b.confidence || 'low'] || 0) - (confMap[a.confidence || 'low'] || 0);
                                              });
                                              fieldValue = sorted[0].value;
                                              sourceFile = sorted[0].fileName || '';
                                              confidence = sorted[0].confidence || 'low';
                                            } else if (fact.value !== null && fact.value !== undefined && fact.value !== '') {
                                              fieldValue = fact.value;
                                              sourceFile = fact.extractedFrom || '';
                                              confidence = typeof fact.confidence === 'string' ? fact.confidence : 'low';
                                            }
                                          }
                                          
                                          const formatValue = (val: any): string => {
                                            if (val === null || val === undefined) return 'Not captured';
                                            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                                            if (Array.isArray(val)) return val.map((item: any) => String(item ?? '').trim()).filter(Boolean).join(', ') || '—';
                                            // Handle Date objects
                                            if (val instanceof Date) return val.toLocaleDateString();
                                            // Handle Firestore Timestamps (they have toDate method)
                                            if (val && typeof val.toDate === 'function') {
                                              try {
                                                return val.toDate().toLocaleDateString();
                                              } catch (e) {
                                                // If toDate fails, try to parse as string
                                                return String(val);
                                              }
                                            }
                                            // Handle objects that might be dates (check for common date properties)
                                            if (typeof val === 'object' && val !== null) {
                                              // If it's an object, try to stringify it properly
                                              // But first check if it looks like a date object
                                              if (val.seconds !== undefined || val._seconds !== undefined) {
                                                // Likely a Firestore Timestamp
                                                try {
                                                  const date = val.toDate ? val.toDate() : new Date(val.seconds * 1000 || val._seconds * 1000);
                                                  return date.toLocaleDateString();
                                                } catch (e) {
                                                  // Fall through to string conversion
                                                }
                                              }
                                              // For other objects, try JSON.stringify but limit length
                                              const str = JSON.stringify(val);
                                              if (str.length > 100) {
                                                return str.substring(0, 100) + '...';
                                              }
                                              return str;
                                            }
                                            return String(val);
                                          };
                                          
                                          return (
                                            <div key={`${evDefIdx}-${fieldIdx}`} className="bg-gray-50 border border-gray-200 rounded p-2 text-xs">
                                              <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                  <div className="flex items-center gap-2">
                                                    <div className="font-medium text-gray-700">
                                                      {fieldDef.label || fieldDef.key}
                                                      {fieldDef.required && <span className="text-red-600 ml-1">*</span>}
                                                    </div>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingField({
                                                          itemText: item.text,
                                                          fieldKey: fieldDef.key,
                                                          fieldDef: fieldDef
                                                        });
                                                      }}
                                                      className="text-blue-600 hover:text-blue-800 text-[10px] px-1.5 py-0.5 rounded hover:bg-blue-50"
                                                      title="Edit field value"
                                                    >
                                                      <Pencil className="w-3 h-3" />
                                                    </button>
                                                  </div>
                                                  <div className="text-gray-600 mt-1">
                                                    {formatValue(fieldValue)}
                                                  </div>
                                                  {isManuallySet && (() => {
                                                    const formatOverrideDate = (dateValue: any): string => {
                                                      if (!dateValue) return '';
                                                      if (dateValue instanceof Date) {
                                                        return dateValue.toLocaleDateString();
                                                      }
                                                      if (dateValue && typeof dateValue.toDate === 'function') {
                                                        try {
                                                          return dateValue.toDate().toLocaleDateString();
                                                        } catch (e) {
                                                          return '';
                                                        }
                                                      }
                                                      if (typeof dateValue === 'object' && dateValue !== null) {
                                                        if (dateValue.seconds !== undefined || dateValue._seconds !== undefined) {
                                                          try {
                                                            const seconds = dateValue.seconds || dateValue._seconds || 0;
                                                            return new Date(seconds * 1000).toLocaleDateString();
                                                          } catch (e) {
                                                            return '';
                                                          }
                                                        }
                                                      }
                                                      return '';
                                                    };
                                                    
                                                    const extractUserFromReason = (reason: string): string => {
                                                      if (!reason) return '';
                                                      if (reason.includes('by ')) {
                                                        const userPart = reason.split('by ')[1];
                                                        if (userPart) {
                                                          const userInfo = userPart.split(' from')[0] || userPart.split(' on')[0] || userPart;
                                                          return userInfo.trim();
                                                        }
                                                      }
                                                      return reason;
                                                    };
                                                    
                                                    return (
                                                      <div className="text-purple-600 mt-1 text-[10px] font-medium">
                                                        Manually set
                                                        {overrideReason && (
                                                          <span className="text-gray-600 font-normal ml-1">
                                                            by {extractUserFromReason(overrideReason)}
                                                          </span>
                                                        )}
                                                        {overrideAt && formatOverrideDate(overrideAt) && (
                                                          <span className="text-gray-500 font-normal ml-1">
                                                            on {formatOverrideDate(overrideAt)}
                                                          </span>
                                                        )}
                                                      </div>
                                                    );
                                                  })()}
                                                  {sourceFile && !isManuallySet && (
                                                    <div className="text-gray-500 mt-1 italic">
                                                      From: {fileUrlMap[sourceFile] ? (
                                                        <a
                                                          href={fileUrlMap[sourceFile]}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="text-blue-600 hover:text-blue-800 hover:underline"
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          {sourceFile}
                                                        </a>
                                                      ) : (
                                                        <span>{sourceFile}</span>
                                                      )}
                                                      <span className={`ml-2 px-1 py-0.5 rounded text-[10px] ${
                                                        confidence === 'high' ? 'bg-green-100 text-green-800' :
                                                        confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'
                                                      }`}>
                                                        {confidence} confidence
                                                      </span>
                                                    </div>
                                                  )}
                                                  {sourceFile && isManuallySet && (
                                                    <div className="text-gray-500 mt-1 italic">
                                                      From: {fileUrlMap[sourceFile] ? (
                                                        <a
                                                          href={fileUrlMap[sourceFile]}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="text-blue-600 hover:text-blue-800 hover:underline"
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          {sourceFile}
                                                        </a>
                                                      ) : (
                                                        <span>{sourceFile}</span>
                                                      )}
                                                      <span className={`ml-2 px-1 py-0.5 rounded text-[10px] ${
                                                        confidence === 'high' ? 'bg-green-100 text-green-800' :
                                                        confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'
                                                      }`}>
                                                        {confidence} confidence
                                                      </span>
                                                    </div>
                                                  )}
                                                  {!sourceFile && fieldValue === null && (
                                                    <div className="text-gray-400 mt-1 italic">No value captured</div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })
                                        ) : (
                                          <div key={evDefIdx} className="bg-gray-50 border border-gray-200 rounded p-2 text-xs">
                                            <div className="font-medium text-gray-700">
                                              {evDef.evidenceName || (evDef as any).categoryCode || 'Unknown'}
                                              {evDef.required && <span className="text-red-600 ml-1">*</span>}
                                            </div>
                                            <div className="text-gray-500 mt-1 italic">No fields — workflow-based</div>
                                            {evDef.workflowRules && (
                                              <div className="text-blue-600 text-[11px] mt-1 bg-blue-50 p-1 rounded border border-blue-200">
                                                <strong>Workflow Rules:</strong> {evDef.workflowRules}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        </React.Fragment>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                  );
                })()}

              </div>
            )}
          </div>
        </div>
      )}

      {/* Field Edit Modal */}
      {editingField && selectedAssetForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Field: {editingField.fieldDef.label || editingField.fieldKey}
              </h3>
              <button
                onClick={() => setEditingField(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <FieldEditModal
              field={editingField}
              asset={selectedAssetForDetails}
              availableFiles={availableFiles}
              assetFacts={assetFacts}
              currentValue={(() => {
                const fact = assetFacts[editingField.fieldKey];
                let value: any = null;
                let file = '';
                
                if (fact) {
                  // Priority: REVIEWER_OVERRIDE > extractedValues > fact.value
                  if (fact.source === 'REVIEWER_OVERRIDE' && fact.value !== null && fact.value !== undefined && fact.value !== '') {
                    // Reviewer override takes highest priority
                    value = fact.value;
                    file = fact.extractedFrom || fact.overrideReason?.match(/from file:\s*([^\n]+)/i)?.[1] || '';
                  } else if (fact.extractedValues && fact.extractedValues.length > 0) {
                    const sorted = fact.extractedValues.sort((a: any, b: any) => {
                      const confMap: Record<string, number> = { high: 3, medium: 2, low: 1 };
                      return (confMap[b.confidence || 'low'] || 0) - (confMap[a.confidence || 'low'] || 0);
                    });
                    value = sorted[0].value;
                    file = sorted[0].fileName || '';
                  } else if (fact.value !== null && fact.value !== undefined && fact.value !== '') {
                    value = fact.value;
                    file = fact.extractedFrom || '';
                  }
                  
                  // Convert Timestamps to Date objects for date fields
                  if (editingField.fieldDef.type === 'date' && value) {
                    // Check if it's already a Date
                    if (value instanceof Date) {
                      // Already a Date, check if valid
                      if (isNaN(value.getTime())) {
                        value = null;
                      }
                    } else if (value && typeof value.toDate === 'function') {
                      // Firestore Timestamp with toDate method
                      try {
                        value = value.toDate();
                        console.log('[HomePage] Converted Timestamp to Date via toDate():', value);
                      } catch (e) {
                        console.warn('[HomePage] Failed to convert Timestamp to Date:', e, value);
                        value = null;
                      }
                    } else if (typeof value === 'object' && value !== null) {
                      // Check if it's a Timestamp-like object with seconds
                      if (value.seconds !== undefined || value._seconds !== undefined) {
                        try {
                          const seconds = value.seconds || value._seconds || 0;
                          value = new Date(seconds * 1000);
                          console.log('[HomePage] Converted Timestamp object to Date from seconds:', value, 'seconds:', seconds);
                        } catch (e) {
                          console.warn('[HomePage] Failed to convert Timestamp object to Date:', e, value);
                          value = null;
                        }
                      } else {
                        // It's an object but not a Timestamp - log it
                        console.warn('[HomePage] Date field has non-Timestamp object value:', value);
                        value = null;
                      }
                    } else if (typeof value === 'string') {
                      const date = new Date(value);
                      if (!isNaN(date.getTime())) {
                        value = date;
                        console.log('[HomePage] Converted date string to Date:', value);
                      } else {
                        console.warn('[HomePage] Invalid date string:', value);
                        value = null;
                      }
                    } else {
                      console.warn('[HomePage] Date field has unexpected value type:', typeof value, value);
                      value = null;
                    }
                  }
                }
                
                console.log('[HomePage] Current value for field edit:', {
                  fieldKey: editingField.fieldKey,
                  fieldType: editingField.fieldDef.type,
                  value,
                  valueType: typeof value,
                  isDate: value instanceof Date,
                  valueKeys: value && typeof value === 'object' ? Object.keys(value) : 'N/A',
                  valueSeconds: value && typeof value === 'object' ? (value.seconds || value._seconds) : 'N/A',
                  file
                });
                
                return { value, file };
              })()}
              onSave={async (newValue, selectedFile) => {
                if (!selectedAssetForDetails.id || !currentOrganization?.id || !user?.id) {
                  console.error('[HomePage] Missing required data for saving:', {
                    assetId: selectedAssetForDetails.id,
                    orgId: currentOrganization?.id,
                    userId: user?.id
                  });
                  showNotification('Missing required information. Please try again.', 'error');
                  return;
                }
                
                try {
                  console.log('[HomePage] Saving field value:', {
                    assetId: selectedAssetForDetails.id,
                    fieldKey: editingField.fieldKey,
                    value: newValue,
                    selectedFile,
                    source: 'REVIEWER_OVERRIDE'
                  });
                  
                  const result = await assetFactsService.setFact(
                    selectedAssetForDetails.id,
                    editingField.fieldKey,
                    newValue,
                    'REVIEWER_OVERRIDE',
                    {
                      organizationId: currentOrganization.id,
                      extractedFrom: selectedFile || undefined,
                      confidence: 'high',
                      overrideReason: `Manual entry by ${user.name || user.email}${selectedFile ? ` from file: ${selectedFile}` : ''}`
                    }
                  );
                  
                  if (!result.success) {
                    throw new Error(result.error || 'Failed to save field value');
                  }
                  
                  console.log('[HomePage] Field saved successfully, reloading facts...');
                  
                  // Reload facts
                  const factsResult = await assetFactsService.getAssetFacts(selectedAssetForDetails.id);
                  if (factsResult.success && factsResult.data) {
                    const factsRecord: Record<string, any> = {};
                    if (factsResult.data.facts) {
                      Object.entries(factsResult.data.facts).forEach(([key, fact]) => {
                        factsRecord[key] = fact;
                      });
                    }
                    
                    const savedFact = factsRecord[editingField.fieldKey];
                    console.log('[HomePage] Updated assetFacts state:', {
                      fieldKey: editingField.fieldKey,
                      newFact: savedFact,
                      factValue: savedFact?.value,
                      factValueType: typeof savedFact?.value,
                      isDate: savedFact?.value instanceof Date,
                      factValueString: savedFact?.value?.toString(),
                      extractedValues: savedFact?.extractedValues
                    });
                    
                    setAssetFacts(factsRecord);
                    
                    // Also reload evidence to reflect the updated field value
                    if (selectedCompletionForDetails?.id) {
                      // Trigger a refresh of the evidence list by reloading it
                      const item = checklistDetails?.items?.find(i => 
                        i.evidenceDefinitions?.some(ed => 
                          ed.fieldDefinitions?.some(fd => fd.key === editingField.fieldKey)
                        )
                      );
                      if (item && selectedCompletionForDetails.id) {
                        try {
                          const evidenceResult = await checklistEvidenceService.getEvidenceByItemResult(
                            selectedCompletionForDetails.id,
                            item.text
                          );
                          if (evidenceResult.success && evidenceResult.data) {
                            setItemEvidenceMap(prev => ({
                              ...prev,
                              [item.text]: evidenceResult.data || []
                            }));
                          }
                        } catch (error) {
                          console.error('[HomePage] Error reloading evidence after field save:', error);
                        }
                      }
                    }
                  } else {
                    console.warn('[HomePage] Failed to reload facts after save:', factsResult.error);
                  }
                  
                  showNotification('Field value updated successfully', 'success');
                  setEditingField(null);
                } catch (error: any) {
                  console.error('[HomePage] Error updating field value:', error);
                  showNotification(`Failed to update field: ${error.message || 'Unknown error'}`, 'error');
                  throw error; // Re-throw so modal can handle it
                }
              }}
              onCancel={() => setEditingField(null)}
            />
          </div>
        </div>
      )}

      {/* Tasks Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Tasks</h3>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* My Tasks Filter - Only show for managers/admins, not for system role "user" */}
            {!isSystemUser && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterMyTasks}
                  onChange={(e) => setFilterMyTasks(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">My Tasks Only</span>
              </label>
            )}

            {/* Location Filter */}
            {hasActiveFilters ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm bg-primary-50 border border-primary-300 rounded-lg">
                <span className="text-primary-900 font-medium">
                  Filtered: {selectedLocationIds.length} location{selectedLocationIds.length !== 1 ? 's' : ''}
                </span>
                <span className="text-primary-700 text-xs">
                  ({availableLocations
                    .filter(loc => selectedLocationIds.includes(loc.id!))
                    .map(loc => loc.name)
                    .slice(0, 2)
                    .join(', ')}
                  {selectedLocationIds.length > 2 && ` + ${selectedLocationIds.length - 2} more`})
                </span>
              </div>
            ) : (
            <select
              value={taskFilterLocation}
              onChange={(e) => setTaskFilterLocation(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Locations</option>
              {Array.from(new Set(tasks.map(t => t.locationName))).sort().map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
            )}

            {/* Risk Level Filter */}
            <select
              value={taskFilterRiskLevel}
              onChange={(e) => setTaskFilterRiskLevel(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Risk Levels</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  ID
                </th>
                <th 
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-72"
                  onClick={() => {
                    if (taskSortBy === 'task') {
                      setTaskSortOrder(taskSortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setTaskSortBy('task');
                      setTaskSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Task
                    {taskSortBy === 'task' ? (taskSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th 
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-24"
                  onClick={() => {
                    if (taskSortBy === 'risk') {
                      setTaskSortOrder(taskSortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setTaskSortBy('risk');
                      setTaskSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Risk Level
                    {taskSortBy === 'risk' ? (taskSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th 
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    if (taskSortBy === 'location') {
                      setTaskSortOrder(taskSortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setTaskSortBy('location');
                      setTaskSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Site
                    {taskSortBy === 'location' ? (taskSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th 
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    if (taskSortBy === 'category') {
                      setTaskSortOrder(taskSortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setTaskSortBy('category');
                      setTaskSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Category
                    {taskSortBy === 'category' ? (taskSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Owner
                </th>
                <th 
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-24"
                  onClick={() => {
                    if (taskSortBy === 'due') {
                      setTaskSortOrder(taskSortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setTaskSortBy('due');
                      setTaskSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Due
                    {taskSortBy === 'due' ? (taskSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th 
                  className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-24"
                  onClick={() => {
                    if (taskSortBy === 'status') {
                      setTaskSortOrder(taskSortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setTaskSortBy('status');
                      setTaskSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {taskSortBy === 'status' ? (taskSortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                  </div>
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTasks.map((task, index) => {
                return (
                  <tr 
                    key={task.id} 
                    className="hover:bg-gray-50"
                  >
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-2 py-2 text-sm font-medium text-gray-900 max-w-72 break-words">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-sm text-gray-900 flex items-center gap-2">
                          <span>{task.assetName ? `${task.assetName} - ` : ''}{task.templateName}</span>
                          {/* Reference Image Thumbnails */}
                          {task.referenceImageUrls && task.referenceImageUrls.length > 0 && (
                            <div className="flex items-center gap-1">
                              {task.referenceImageUrls.slice(0, 3).map((imageUrl, imgIndex) => {
                                // Find metadata for this image to get the note
                                const imageMetadata = task.referenceImageMetadata?.find(meta => meta.url === imageUrl);
                                const note = imageMetadata?.note;
                                const tooltipText = note 
                                  ? `Image ${imgIndex + 1} of ${task.referenceImageUrls?.length || 0}\nNote: ${note}`
                                  : `Click to view image ${imgIndex + 1} of ${task.referenceImageUrls?.length || 0}`;
                                
                                return (
                                  <img
                                    key={imgIndex}
                                    src={imageUrl}
                                    alt={`Reference ${imgIndex + 1}`}
                                    className="w-8 h-8 object-cover rounded border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const imageUrls = task.referenceImageUrls || [];
                                      setViewingImageUrls(imageUrls);
                                      setViewingImageIndex(imgIndex);
                                      setViewingImageUrl(imageUrl);
                                      setViewingTask(task);
                                    }}
                                    title={tooltipText}
                                  />
                                );
                              })}
                              {(task.referenceImageUrls?.length || 0) > 3 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const imageUrls = task.referenceImageUrls || [];
                                    if (imageUrls.length > 0) {
                                      setViewingImageUrls(imageUrls);
                                      setViewingImageIndex(0);
                                      setViewingImageUrl(imageUrls[0]);
                                      setViewingTask(task);
                                    }
                                  }}
                                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 bg-gray-100 text-xs font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                                  title={`View all ${task.referenceImageUrls?.length || 0} images`}
                                >
                                  +{(task.referenceImageUrls?.length || 0) - 3}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {!task.templateId || task.templateId === null || task.templateId === '' ? (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 whitespace-nowrap">
                            Custom
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300 whitespace-nowrap">
                            Compliance
                          </span>
                        )}
                        {task.rejectedBy && task.rejectionReason && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setRejectedTask(task);
                              setShowRejectionModal(true);
                            }}
                            className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-300 whitespace-nowrap flex items-center gap-1 hover:bg-red-200 hover:border-red-400 transition-colors cursor-pointer"
                          >
                            <XCircle className="w-3 h-3" />
                            Previously Rejected
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-700">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        task.riskLevel?.toLowerCase() === 'high' ? 'bg-red-100 text-red-800' :
                        task.riskLevel?.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {task.riskLevel || 'Low'}
                      </span>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-700">
                      {task.locationName}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-700">
                      {task.category}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-700">
                      <div className="space-y-1">
                        {(() => {
                          // Priority 1: Use assignedUserIds if available
                          // Handle null, undefined, and ensure it's an array
                          const assignedUserIds = (task.assignedUserIds && Array.isArray(task.assignedUserIds) && task.assignedUserIds.length > 0) 
                            ? task.assignedUserIds 
                            : (task.assignedUserId ? [task.assignedUserId] : []);
                          let responsibleUsers: Array<{ id: string; email: string; name?: string }> = [];
                          
                          if (assignedUserIds.length > 0) {
                            // Use explicitly assigned users
                            responsibleUsers = allUsers.filter(user => assignedUserIds.includes(user.id));
                            // When we have explicit assignments, show "Assigned Users" instead of owner role
                            return (
                              <>
                                <div className="font-medium text-gray-900">
                                  Assigned Users
                                </div>
                                {responsibleUsers.length === 0 ? (
                                  <div className="text-xs text-gray-500 italic">
                                    No users assigned
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-600 space-y-0.5">
                                    {responsibleUsers.map(user => (
                                      <div key={user.id} className="truncate max-w-[200px]" title={user.email}>
                                        {user.name || user.email}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            );
                          } else {
                            // Fallback to ownerRole-based logic (backward compatibility)
                            const taskOwnerRole = task.templateSnapshot?.owner_role || task.ownerRole;
                            
                            // Find users who have this owner role AND access to the task's location
                            if (taskOwnerRole) {
                              responsibleUsers = allUsers.filter(user => {
                                const userRoles = normalizeRoles(user.roles || user.role);
                                const hasOwnerRole = userRoles.includes(taskOwnerRole);
                                
                                // Also check if user has access to this task's location
                                const userLocationIds = user.locationIds || [];
                                const hasLocationAccess = task.locationId && userLocationIds.includes(task.locationId);
                                
                                // User must have both the owner role AND location access
                                return hasOwnerRole && hasLocationAccess;
                              });
                            }
                            
                            return (
                              <>
                                <div className="font-medium text-gray-900">
                                  {taskOwnerRole || 'Not assigned'}
                                </div>
                                {responsibleUsers.length === 0 ? (
                                  <div className="text-xs text-gray-500 italic">
                                    No users assigned
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-600 space-y-0.5">
                                    {responsibleUsers.map(user => (
                                      <div key={user.id} className="truncate max-w-[200px]" title={user.email}>
                                        {user.name || user.email}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            );
                          }
                        })()}
                      </div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-700">
                      {task.dueDate.toLocaleDateString()}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(task.status)}`}>
                        {task.status === 'overdue' && <AlertTriangle className="inline w-3 h-3 mr-1" />}
                        {task.status === 'pending_review' ? 'Pending Review' :
                         task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-center">
                      {task.status === 'pending_review' && (
                        <button
                          onClick={() => handleReviewTask(task)}
                          disabled={!isUserManager}
                          className={`text-xs px-2 py-1 rounded flex items-center gap-1 mx-auto ${
                            isUserManager
                              ? 'bg-orange-600 text-white hover:bg-orange-700'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                          }`}
                          title={!isUserManager ? "Only admins and managers can review tasks" : "Review Task"}
                        >
                          <FileCheck className="w-3 h-3" />
                          Review
                        </button>
                      )}
                      {task.status !== 'pending_review' && task.status !== 'completed' && (
                        <button
                          onClick={() => handleCompleteTask(task)}
                          className="bg-blue-600 text-white text-xs px-2 py-1 rounded hover:bg-blue-700 flex items-center gap-1 mx-auto"
                          title="Complete Task"
                        >
                          <Check className="w-3 h-3" />
                          Complete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
            {((hasActiveFilters && selectedLocationIds.length > 0) || taskFilterLocation !== 'all') && ` (filtered by location)`}
            {taskFilterRiskLevel !== 'all' && ` (filtered by risk level)`}
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-gray-400" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* High Risk Tasks Modal */}
      {showHighRiskModal && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-500 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold">High Risk Tasks</h3>
                <p className="text-sm text-red-100 mt-1">{selectedLocation}</p>
              </div>
              <button 
                onClick={() => {
                  setShowHighRiskModal(false);
                  setSelectedLocation(null);
                }} 
                className="text-white hover:bg-red-600 rounded-full p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-150px)]">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tasks
                      .filter(task =>
                        task.locationName === selectedLocation &&
                        isHighRiskDueSoonOrOverdue(task)
                      )
                      .map((task) => {
                        return (
                          <tr key={task.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span>{task.templateName}</span>
                                {!task.templateId || task.templateId === null || task.templateId === '' ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 whitespace-nowrap">
                                    Custom
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300 whitespace-nowrap">
                                    Compliance
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                              {task.category}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                              {task.dueDate.toLocaleDateString()}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(task.status)}`}>
                                {task.status === 'overdue' && <AlertTriangle className="inline w-3 h-3 mr-1" />}
                                {task.status === 'pending_review' ? 'Pending Review' :
                                 task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {tasks.filter(task =>
                task.locationName === selectedLocation &&
                isHighRiskDueSoonOrOverdue(task)
              ).length === 0 && (
                <div className="text-center py-12">
                  <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No high-risk tasks found</p>
                  <p className="text-gray-400 text-sm mt-2">All high-risk tasks may have been completed</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowHighRiskModal(false);
                  setSelectedLocation(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Scorecard Modal */}
      {showScorecardModal && (() => {
        const allLocations = Array.from(new Set(tasksForComplianceScorecard.map(t => t.locationName)));
        const locationsWithScores = allLocations
          .map(locationName => ({
            name: locationName,
            score: locationScores.get(locationName) ?? 100
          }))
          .sort((a, b) => b.score - a.score);

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowScorecardModal(false)}>
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-gray-600" />
                    Compliance Scorecard
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">{locationsWithScores.length} Location{locationsWithScores.length !== 1 ? 's' : ''}</p>
                </div>
                <button 
                  onClick={() => setShowScorecardModal(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-150px)]">
                {locationsWithScores.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-lg">No locations with tasks</p>
                    <p className="text-sm mt-2">Assign templates to locations to see scores</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {locationsWithScores.map(({ name, score }) => (
                      <div
                        key={name}
                        onClick={() => {
                          setSelectedLocationForDetails(name);
                          setShowScorecardModal(false);
                        }}
                        className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-blue-300 transition-all cursor-pointer"
                        title="Click to view compliance score details"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Building2 className="w-5 h-5 text-gray-600 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate" title={name}>
                            {name}
                          </span>
                        </div>
                        <div className={`px-4 py-2 rounded border text-sm font-semibold flex-shrink-0 ${getScoreBadgeClass(score)}`}>
                          {score.toFixed(1)}%
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Showing {locationsWithScores.length} location{locationsWithScores.length !== 1 ? 's' : ''}
                </div>
                <button
                  onClick={() => setShowScorecardModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Site Metrics Modal */}
      {showSiteMetricsModal && (() => {
        // Calculate metrics per location
        const locationMetrics: Record<string, { dueSoon: number; pastDue: number; highRisk: number; pendingReview: number }> = {};
        
        // Filter out completed tasks first
        const activeTasks = tasks.filter(task => task.status !== 'completed');
        
        const now = new Date();
        
        activeTasks.forEach(task => {
          if (!locationMetrics[task.locationName]) {
            locationMetrics[task.locationName] = { dueSoon: 0, pastDue: 0, highRisk: 0, pendingReview: 0 };
          }
          
          const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
          const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          
          // Count due soon items (pending tasks due within next 30 days)
          if (task.status === 'pending' && daysUntilDue >= 0 && daysUntilDue <= 30) {
            locationMetrics[task.locationName].dueSoon++;
          }
          
          // Count past due items
          if (task.status === 'overdue') {
            locationMetrics[task.locationName].pastDue++;
          }
          
          // Count pending review items
          if (task.status === 'pending_review') {
            locationMetrics[task.locationName].pendingReview++;
          }
          
          if (isHighRiskDueSoonOrOverdue(task)) {
            locationMetrics[task.locationName].highRisk++;
          }
        });

        // Sort locations by due soon items
        const allLocations = Object.entries(locationMetrics)
          .sort((a, b) => b[1].dueSoon - a[1].dueSoon);

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowSiteMetricsModal(false)}>
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Site Metrics</h3>
                  <p className="text-sm text-gray-500 mt-1">{allLocations.length} Location{allLocations.length !== 1 ? 's' : ''}</p>
                </div>
                <button 
                  onClick={() => setShowSiteMetricsModal(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-150px)]">
                {allLocations.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-lg">No locations with tasks</p>
                    <p className="text-sm mt-2">Assign templates to locations to see metrics</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Soon</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Past Due</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">High Risk</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Review</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allLocations.map(([locationName, metrics]) => (
                          <tr key={locationName} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {locationName}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                              {metrics.dueSoon}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                              {metrics.pastDue}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <button
                                onClick={() => {
                                  setSelectedLocation(locationName);
                                  setShowSiteMetricsModal(false);
                                  setShowHighRiskModal(true);
                                }}
                                className={`text-sm font-semibold ${
                                  metrics.highRisk > 0 
                                    ? 'text-red-600 hover:text-red-700 hover:underline cursor-pointer' 
                                    : 'text-gray-400 cursor-default'
                                }`}
                                disabled={metrics.highRisk === 0}
                              >
                                {metrics.highRisk}
                              </button>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <button
                                onClick={() => {
                                  setSelectedLocation(locationName);
                                  setShowSiteMetricsModal(false);
                                  setShowPendingReviewModal(true);
                                }}
                                className={`text-sm font-semibold ${
                                  metrics.pendingReview > 0 
                                    ? 'text-orange-600 hover:text-orange-700 hover:underline cursor-pointer' 
                                    : 'text-gray-400 cursor-default'
                                }`}
                                disabled={metrics.pendingReview === 0}
                              >
                                {metrics.pendingReview}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Showing {allLocations.length} location{allLocations.length !== 1 ? 's' : ''}
                </div>
                <button
                  onClick={() => setShowSiteMetricsModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pending Review Tasks Modal */}
      {showPendingReviewModal && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold">Pending Review Tasks</h3>
                <p className="text-sm text-orange-100 mt-1">{selectedLocation}</p>
              </div>
              <button 
                onClick={() => {
                  setShowPendingReviewModal(false);
                  setSelectedLocation(null);
                }} 
                className="text-white hover:bg-orange-600 rounded-full p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-150px)]">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tasks
                      .filter(task => 
                        task.locationName === selectedLocation && 
                        task.status === 'pending_review'
                      )
                      .map((task) => {
                        return (
                          <tr key={task.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span>{task.templateName}</span>
                                {!task.templateId || task.templateId === null || task.templateId === '' ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 whitespace-nowrap">
                                    Custom
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300 whitespace-nowrap">
                                    Compliance
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                              {task.category}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                              {task.dueDate.toLocaleDateString()}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                Pending Review
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {tasks.filter(task => 
                task.locationName === selectedLocation && 
                task.status === 'pending_review'
              ).length === 0 && (
                <div className="text-center py-12">
                  <FileCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No pending review tasks found</p>
                  <p className="text-gray-400 text-sm mt-2">All tasks have been reviewed or completed</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowPendingReviewModal(false);
                  setSelectedLocation(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Task Modal */}
      {showCompleteModal && selectedTask && (
        <CompleteTaskModal
          task={selectedTask}
          onClose={() => {
            setShowCompleteModal(false);
            setSelectedTask(null);
          }}
          onComplete={async (_data: TaskCompletionData) => {
            if (selectedTask && user) {
              try {
                // Mark task as completed in the tasks table
                const result = await completeTask(selectedTask.id, user.id);
                if (result.success) {
                  console.log('✅ Task marked as completed in tasks table');
                  // Reload tasks after completion
                  loadDashboardData();
                } else {
                  console.error('Failed to mark task as completed:', result.error);
                }
              } catch (error) {
                console.error('Error completing task:', error);
              }
            }
            setShowCompleteModal(false);
            setSelectedTask(null);
          }}
        />
      )}

      {/* Location Score Details Right Panel */}
      {selectedLocationForDetails && locationScoreDetails.has(selectedLocationForDetails) && (
        <>
          {/* Click-out overlay to close */}
          <div
            className="fixed inset-0 bg-black bg-opacity-20 z-[125]"
            onClick={() => setSelectedLocationForDetails(null)}
          />
          <div className="fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl border-l border-gray-200 z-[130] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  {selectedLocationForDetails}
                </h3>
                <p className="text-sm text-gray-600 mt-1">Compliance Score Details</p>
              </div>
              <button
                onClick={() => setSelectedLocationForDetails(null)}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {(() => {
                const scoreDetails = locationScoreDetails.get(selectedLocationForDetails);
                if (!scoreDetails) return null;

                return (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className={`p-4 rounded-lg border-2 ${getScoreBadgeClass(scoreDetails.score)}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium opacity-75 mb-1">Compliance Score</p>
                          <p className="text-3xl font-bold">{scoreDetails.score.toFixed(1)}%</p>
                          <p className="text-xs mt-2 opacity-75">
                            {scoreDetails.taskCount} task{scoreDetails.taskCount !== 1 ? 's' : ''} counted
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Task Details */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Tasks Included in Calculation</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Complete pending, overdue, or pending review tasks to increase your compliance score
                      </p>
                      {(() => {
                        // Calculate what the score would be if ALL incomplete tasks (including pending_review) were completed
                        const incompleteTasks = scoreDetails.taskScores.filter(ts => 
                          ts.status !== 'completed'
                        );
                        const hasOpenTasks = scoreDetails.openMaxPossibleScore > 0.0001;
                        const scoreIfAllCompleted = hasOpenTasks ? 100 : scoreDetails.score;
                        const incompleteCount = incompleteTasks.length;
                        const increaseAll = scoreIfAllCompleted - scoreDetails.score;
                        
                        if (incompleteCount > 0) {
                          return (
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mb-3">
                              <p className="text-xs font-semibold text-blue-900 mb-1">
                                📊 Complete All {incompleteCount} Incomplete Task{incompleteCount !== 1 ? 's' : ''} (including pending review)
                              </p>
                              <p className="text-xs text-blue-700">
                                Current: <span className="font-semibold">{scoreDetails.score.toFixed(1)}%</span> → 
                                If All Completed: <span className="font-bold text-blue-900">{scoreIfAllCompleted.toFixed(1)}%</span>
                                {' '}
                                <span className="text-green-600 font-medium">
                                  (+{increaseAll > 0 ? increaseAll.toFixed(1) : '0.0'}%)
                                </span>
                              </p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {scoreDetails.taskScores.length === 0 ? (
                        <p className="text-sm text-gray-500">No tasks to display</p>
                      ) : (
                        <div className="space-y-2">
                          {scoreDetails.taskScores.map((taskScore) => {
                              const clampValue = (value: number) => Math.max(0, Math.min(100, value));
                              const isIncomplete = taskScore.status !== 'completed' && taskScore.status !== 'pending_review';
                              const isPendingReview = taskScore.status === 'pending_review';

                              // Full task (includes items not in the user’s visible task list) for permissions + labels
                              const actualTask = tasksForComplianceScorecard.find(t => t.id === taskScore.taskId);
                              const taskOwnerRole = actualTask?.templateSnapshot?.owner_role || actualTask?.ownerRole;
                              const isCustomTask = !actualTask?.templateId || actualTask?.templateId === null || actualTask?.templateId === '';
                              
                              // Check if user can complete this task (for system "User" role users)
                              const organizationType = currentOrganization?.industryType || null;
                              const canCompleteTask = isUserManager || !isSystemUser || (taskOwnerRole && taskMatchesUserOwnerRoles(taskOwnerRole, userOwnerRoles, organizationType));
                              // Only admins and managers can review tasks
                              const canReviewTask = isUserManager;

                              const showRestrictedTaskRow =
                                (isIncomplete && !canCompleteTask) ||
                                (isPendingReview && !canReviewTask);

                              const restrictedHelperText =
                                isPendingReview && !canReviewTask
                                  ? 'Only admins or managers can review this task.'
                                  : 'Only users with the assigned owner role can complete this task.';

                              const ownerInfo = getComplianceScoreTaskOwnerDisplay(
                                actualTask,
                                allUsers,
                                currentOrganization?.id
                              );

                              if (showRestrictedTaskRow) {
                                return (
                                  <div
                                    key={taskScore.taskId}
                                    className="p-3 border border-gray-200 rounded-lg bg-gray-50 opacity-75"
                                  >
                                    <p className="text-sm font-medium text-gray-400">
                                      {taskScore.taskName}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">{restrictedHelperText}</p>
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                      <div className="flex items-start gap-1.5 text-xs text-gray-500">
                                        <User className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                                        <div className="min-w-0">
                                          <span className="font-medium text-gray-600">{ownerInfo.title}</span>
                                          {ownerInfo.names.length > 0 && (
                                            <ul className="mt-0.5 space-y-0.5">
                                              {ownerInfo.names.map((n, idx) => (
                                                <li key={idx} className="truncate" title={n}>
                                                  {n}
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              let newScoreClamped = scoreDetails.score;
                              // Calculate score increase for both incomplete and pending_review tasks (both are not completed)
                              if (isIncomplete || isPendingReview) {
                                const updatedCompletedScore = scoreDetails.completedScore + taskScore.maxPossibleScore;
                                const updatedOpenActualScore = scoreDetails.openActualScore - taskScore.taskScore;
                                const updatedOpenMinScore = scoreDetails.openMinPossibleScore - taskScore.minContribution;
                                const updatedOpenMaxScore = scoreDetails.openMaxPossibleScore - taskScore.maxPossibleScore;

                                const updatedOpenRange = updatedOpenMaxScore - updatedOpenMinScore;
                                let updatedNormalizedOpen = 0;
                                if (updatedOpenMaxScore <= 0) {
                                  updatedNormalizedOpen = 0;
                                } else if (updatedOpenRange <= 0) {
                                  updatedNormalizedOpen = updatedOpenActualScore >= updatedOpenMaxScore ? 1 : 0;
                                } else {
                                  updatedNormalizedOpen = (updatedOpenActualScore - updatedOpenMinScore) / updatedOpenRange;
                                  updatedNormalizedOpen = Math.max(0, Math.min(1, updatedNormalizedOpen));
                                }

                                const updatedDenominator = updatedCompletedScore + updatedOpenMaxScore;
                                if (updatedDenominator > 0) {
                                  newScoreClamped = clampValue(
                                    ((updatedCompletedScore + updatedNormalizedOpen * updatedOpenMaxScore) / updatedDenominator) * 100
                                  );
                                } else {
                                  newScoreClamped = 100;
                                }
                              }

                              const scoreIncreasePercentage = newScoreClamped - scoreDetails.score;

                              const getStatusIcon = () => {
                                switch (taskScore.status) {
                                  case 'completed':
                                    return <CheckCircle2 className="w-4 h-4 text-green-600" />;
                                  case 'pending_review':
                                    return <Clock className="w-4 h-4 text-orange-600" />;
                                  case 'overdue':
                                    return <AlertTriangle className="w-4 h-4 text-red-600" />;
                                  default:
                                    return <Clock className="w-4 h-4 text-gray-600" />;
                                }
                              };

                              return (
                                <div
                                  key={taskScore.taskId}
                                  className="p-3 border border-gray-200 rounded-lg"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2 flex-1 min-w-0">
                                      {getStatusIcon()}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {taskScore.taskName}
                                        </p>
                                          {isCustomTask ? (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 whitespace-nowrap">
                                              Custom
                                            </span>
                                          ) : (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-300 whitespace-nowrap">
                                              Compliance
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                                          <span className="capitalize">{taskScore.status.replace('_', ' ')}</span>
                                          <span>•</span>
                                          <span>{taskScore.riskLevel} Risk</span>
                                          {taskScore.isDueSoon && (
                                            <>
                                              <span>•</span>
                                              <span className="text-blue-600 font-medium">Due Soon</span>
                                            </>
                                          )}
                                        </div>
                                        <div className="mt-2 pt-2 border-t border-gray-100">
                                          <div className="flex items-start gap-1.5 text-xs text-gray-600">
                                            <User className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                                            <div className="min-w-0">
                                              <span className="font-medium text-gray-800">{ownerInfo.title}</span>
                                              {ownerInfo.names.length > 0 && (
                                                <ul className="mt-0.5 space-y-0.5">
                                                  {ownerInfo.names.map((n, idx) => (
                                                    <li key={idx} className="truncate" title={n}>
                                                      {n}
                                                    </li>
                                                  ))}
                                                </ul>
                                              )}
                                            </div>
                                          </div>
                                          {isPendingReview && (
                                            <p className="text-xs text-gray-500 mt-1.5 pl-5">
                                              Reviews are completed by admins or managers.
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      {isIncomplete && (
                                        <div className="flex flex-col items-end gap-2">
                                          <div className="text-xs text-green-600 font-medium">
                                            {scoreIncreasePercentage > 0 ? '+' : ''}{scoreIncreasePercentage.toFixed(1)}% if completed
                                            {newScoreClamped > scoreDetails.score && (
                                              <span className="text-gray-500 ml-1">
                                                (→ {newScoreClamped.toFixed(1)}%)
                                              </span>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => canCompleteTask && handleCompleteFromScorecard(taskScore.taskId)}
                                            disabled={!canCompleteTask}
                                            title={
                                              !canCompleteTask
                                                ? 'Only users with the assigned owner role can complete this task.'
                                                : undefined
                                            }
                                            className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                              canCompleteTask
                                                ? 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500'
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                            }`}
                                          >
                                            Complete
                                          </button>
                                        </div>
                                      )}
                                      {isPendingReview && (
                                        <div className="flex flex-col items-end gap-2">
                                          <div className="text-xs text-green-600 font-medium">
                                            {scoreIncreasePercentage > 0 ? '+' : ''}{scoreIncreasePercentage.toFixed(1)}% if reviewed
                                            {newScoreClamped > scoreDetails.score && (
                                              <span className="text-gray-500 ml-1">
                                                (→ {newScoreClamped.toFixed(1)}%)
                                              </span>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => canReviewTask && handleReviewFromScorecard(taskScore.taskId)}
                                            disabled={!canReviewTask}
                                            title={!canReviewTask ? "Only admins and managers can review tasks" : undefined}
                                            className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                              canReviewTask
                                                ? 'bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-500'
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                            }`}
                                          >
                                            Review
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>

                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Review Task Modal */}
      {showReviewModal && selectedTask && (
        <ReviewTaskModal
          task={selectedTask}
          onReview={handleReviewComplete}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedTask(null);
          }}
        />
      )}

      {/* Rejection Details Modal */}
      {showRejectionModal && rejectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[130] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-red-900">Task Rejection Details</h3>
              </div>
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectedTask(null);
                }}
                className="text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                <div>
                  <span className="text-xs font-medium text-gray-500">Task Name</span>
                  <p className="text-sm font-medium text-gray-900 mt-1">{rejectedTask.templateName}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500">Rejected By</span>
                  <p className="text-sm text-gray-900 mt-1">{rejectedTask.rejectedBy}</p>
                </div>
                {rejectedTask.rejectedAt && (
                  <div>
                    <span className="text-xs font-medium text-gray-500">Rejected On</span>
                    <p className="text-sm text-gray-900 mt-1">
                      {(() => {
                        try {
                          const rejectedDate = rejectedTask.rejectedAt?.toDate ? rejectedTask.rejectedAt.toDate() : 
                                              (rejectedTask.rejectedAt instanceof Date ? rejectedTask.rejectedAt :
                                              (typeof rejectedTask.rejectedAt === 'number' ? new Date(rejectedTask.rejectedAt) : new Date(rejectedTask.rejectedAt)));
                          return rejectedDate.toLocaleString();
                        } catch (error) {
                          return 'Invalid Date';
                        }
                      })()}
                    </p>
                  </div>
                )}
                <div>
                  <span className="text-xs font-medium text-gray-500">Reason for Rejection</span>
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-900 whitespace-pre-wrap">{rejectedTask.rejectionReason}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectedTask(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-Screen Image Viewer */}
      {viewingImageUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[250] flex items-center justify-center"
          onClick={() => {
            setViewingImageUrl(null);
            setViewingImageUrls([]);
            setViewingImageIndex(0);
            setViewingTask(null);
          }}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={() => {
                setViewingImageUrl(null);
                setViewingImageUrls([]);
                setViewingImageIndex(0);
                setViewingTask(null);
              }}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
              title="Close (Esc)"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Previous Button */}
            {viewingImageUrls.length > 1 && viewingImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = viewingImageIndex - 1;
                  setViewingImageIndex(newIndex);
                  setViewingImageUrl(viewingImageUrls[newIndex]);
                }}
                className="absolute left-4 z-10 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
                title="Previous image (←)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            {/* Next Button */}
            {viewingImageUrls.length > 1 && viewingImageIndex < viewingImageUrls.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = viewingImageIndex + 1;
                  setViewingImageIndex(newIndex);
                  setViewingImageUrl(viewingImageUrls[newIndex]);
                }}
                className="absolute right-4 z-10 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-colors"
                title="Next image (→)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}

            {/* Image */}
            <img
              src={viewingImageUrl}
              alt={`Reference image ${viewingImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Image Counter and Note */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2">
              {viewingImageUrls.length > 1 && (
                <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm">
                  {viewingImageIndex + 1} / {viewingImageUrls.length}
                </div>
              )}
              {/* Show note if available */}
              {viewingTask?.referenceImageMetadata && viewingImageUrl && (() => {
                const currentImageMetadata = viewingTask.referenceImageMetadata.find(
                  meta => meta.url === viewingImageUrl
                );
                if (currentImageMetadata?.note) {
                  return (
                    <div className="bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm max-w-md text-center">
                      <div className="font-medium mb-1">Note:</div>
                      <div>{currentImageMetadata.note}</div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
